import useSidebarState from '../hooks/useSidebarState';
import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import './MyTeam.css';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, set, push, onValue, update } from 'firebase/database';
import { auth, database, storage } from '../firebase';
import { ref as stRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import FeatureGate from './Trial/FeatureGate';
import AppLoading from './AppLoading';
import { buildEmployeeRecordFromHire } from '../utils/employeeRecords';
import EmployeePage from './MyTeam/EmployeeDrawer/EmployeePage';
import SignedUploadModal from './MyTeam/AgreementsGallery/SignedUploadModal';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { translateDepartment } from '../utils/translationUtils';
import { syncCommunityMember, triggerCommunityAutoSync } from '../services/communityService';
import useTeamAccessProfile from '../hooks/useTeamAccessProfile';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import NationalizationView from './MyTeam/Nationalization/NationalizationView';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange,
  startNationalizationLifecycleReplay
} from '../services/nationalizationService';
import {
  addRegulatoryImportMarkerToUpdates,
  allocateRegulatoryImportId,
  buildCanonicalRegulatoryImportChunks,
  createRegulatoryImportFactsBindingToken,
  getMissingRegulatoryImportIdentityFields,
  hashRegulatoryImportEmployeeIds,
  normalizeRegulatoryImportEmployeeIds,
  normalizeRegulatoryCountryCode,
  normalizeRegulatoryEmploymentStatus,
  parseRegulatoryBoolean,
  removeRegulatoryImportMarker,
  REGULATORY_IMPORT_SCOPES,
  resolveRegulatoryImportEstablishmentId,
  shouldRejectCsvParserErrors
} from '../services/nationalizationRegulatoryImport';

const SIDEBAR_PRIMARY = '#568ded';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const initialRows = [];
const TEAM_VIEWS = new Set(['overview', 'pto', 'compensation', 'performance', 'agreements', 'nationalization']);

const LetterBadge = ({ name, photoUrl }) => {
  useUiLanguage();
  const letter = (name || 'U').slice(0, 1).toUpperCase();
  return (
    <div className="letter-badge">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name || uiText("Employee")}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
          onError={(event) => {
            event.currentTarget.style.display = 'none';
            const fallback = event.currentTarget.nextSibling;
            if (fallback) {
              fallback.style.display = 'inline-flex';
            }
          }}
        />
      ) : null}
      <span style={{ display: photoUrl ? 'none' : 'inline-flex' }}>{letter}</span>
    </div>
  );
};

const ContactInfo = ({ workEmail, email, phone, onOpenDetails }) => {
  const { t } = useTranslation('myteam');
  const [emailCopied, setEmailCopied] = useState(false);
  const [workCopied, setWorkCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        return true;
      } catch (_) {
        return false;
      }
    }
  };

  const onCopyEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    const ok = await copyToClipboard(email);
    if (ok) {
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1200);
    }
  };
  const onCopyWork = async (e) => {
    e.preventDefault();
    if (!workEmail) return;
    const ok = await copyToClipboard(workEmail);
    if (ok) {
      setWorkCopied(true);
      setTimeout(() => setWorkCopied(false), 1200);
    }
  };
  const onCopyPhone = async (e) => {
    e.preventDefault();
    if (!phone) return;
    const ok = await copyToClipboard(phone);
    if (ok) {
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 1200);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      {workEmail && (
        <span className="contact-icon-wrapper">
          <a href={`mailto:${workEmail}`} onClick={onCopyWork} title={workEmail} style={{ color: 'var(--app-text, #FFFFFF)', fontSize: '16px' }}>
            <i className="fa-solid fa-at"></i>
          </a>
          {workCopied && <span className="copied-tooltip">{t('contact.copied', 'copied!')}</span>}
        </span>
      )}
      {email && (
        <span className="contact-icon-wrapper">
          <a href={`mailto:${email}`} onClick={onCopyEmail} title={email} style={{ color: 'var(--app-text, #FFFFFF)', fontSize: '16px' }}>
            <i className="fa-solid fa-envelope"></i>
          </a>
          {emailCopied && <span className="copied-tooltip">{t('contact.copied', 'copied!')}</span>}
        </span>
      )}
      {phone && (
        <span className="contact-icon-wrapper">
          <a href={`tel:${phone}`} onClick={onCopyPhone} title={phone} style={{ color: 'var(--app-text, #FFFFFF)', fontSize: '16px' }}>
            <i className="fa-solid fa-phone"></i>
          </a>
          {phoneCopied && <span className="copied-tooltip">{t('contact.copied', 'copied!')}</span>}
        </span>
      )}
      <span className="contact-icon-wrapper">
        <button onClick={onOpenDetails} title={t('contact.viewDetails', 'View full contact details')} style={{ color: 'var(--app-text, #FFFFFF)', fontSize: '16px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}  data-light-control={"quiet"}>
          <i className="fa-solid fa-circle-info"></i>
        </button>
      </span>
      {(!workEmail && !email && !phone) && <span className="cell-text" style={{ opacity: 0.7 }}>—</span>}
    </div>
  );
};

// Helper to compute inclusive day span between two yyyy-mm-dd dates
const inclusiveDaysBetween = (start, end) => {
  try {
    if (!start || !end) return 0;
    const a = new Date(`${start}T00:00:00`);
    const b = new Date(`${end}T00:00:00`);
    const diffMs = b.getTime() - a.getTime();
    if (Number.isNaN(diffMs)) return 0;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
    return days < 0 ? 0 : days;
  } catch {
    return 0;
  }
};

// A regulatory import must never turn an unknown or misspelled lifecycle state
// into an active worker. These values mirror the backend's explicit current,
// forecast, and inactive vocabularies.
const NATIONALIZATION_EMPLOYMENT_STATUSES = new Set([
  'active',
  'probation',
  'on_probation',
  'on_leave',
  'leave',
  'paid_leave',
  'maternity_leave',
  'paternity_leave',
  'offboarding',
  'onboarding',
  'preboarding',
  'hired',
  'accepted',
  'offer_accepted',
  'pending_start',
  'inactive',
  'offboarded',
  'terminated',
  'cancelled'
]);

const normalizeEmploymentStatus = normalizeRegulatoryEmploymentStatus;
const normalizeCountryCode = normalizeRegulatoryCountryCode;

// Evidence dates are attestations, so they must be real calendar dates and can
// never be in the future. Checking before the roster write avoids a recoverable
// but confusing half-finished regulatory import.
const NATIONALIZATION_TIME_ZONES = Object.freeze({
  AE: 'Asia/Dubai',
  SA: 'Asia/Riyadh',
  QA: 'Asia/Qatar',
  OM: 'Asia/Muscat',
  BH: 'Asia/Bahrain',
  KW: 'Asia/Kuwait'
});

const calendarDateInTimeZone = (timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const isValidEvidenceDate = (value = '', countryCode = '') => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  const isExactDate = parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
  const countryToday = calendarDateInTimeZone(
    NATIONALIZATION_TIME_ZONES[countryCode] || 'UTC'
  );
  return isExactDate && value <= countryToday;
};

// Only errors created from translated copy may be shown directly. Parser,
// network and backend details stay in diagnostics so Arabic screens never end
// with an untranslated implementation message.
const localizedImportError = (message) => {
  const error = new Error(message);
  error.isLocalizedImportError = true;
  return error;
};

const csvValue = (record = {}, aliases = []) => {
  const match = aliases.find((alias) => Object.prototype.hasOwnProperty.call(record, alias));
  return match ? String(record[match] ?? '').trim() : '';
};

const optionalBoolean = parseRegulatoryBoolean;

// Unknown CSV values are omitted instead of being sent as invalid JSON nulls.
// The backend treats each imported row as a full replacement, so an omitted
// value clears stale evidence and makes the calculation provisional. False and
// zero remain valid evidence and are therefore preserved.
const omitUnknownRegulatoryFacts = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(omitUnknownRegulatoryFacts)
      .filter((item) => item !== null && item !== undefined && item !== '');
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .map(([key, item]) => [key, omitUnknownRegulatoryFacts(item)])
        .filter(([, item]) => {
          if (item === null || item === undefined || item === '') return false;
          if (Array.isArray(item)) return item.length > 0;
          if (item && typeof item === 'object') return Object.keys(item).length > 0;
          return true;
        })
    );
  }
  return value;
};

// Some GCC catalogues classify a role at an occupation-group level instead of
// exposing a leaf occupation code. Use one stable UI value for either shape so
// employers can explicitly confirm both kinds of official mapping.
const occupationSuggestionValue = (suggestion = {}) => {
  const code = suggestion.code || suggestion.occupation_code || '';
  const group = suggestion.occupation_group || suggestion.group || '';
  return code ? `code:${code}` : group ? `group:${group}` : '';
};

const setObjectPath = (target, path, value) => {
  const keys = String(path || '').split('.').filter(Boolean);
  if (!keys.length) return target;
  let cursor = target;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
    } else {
      cursor[key] = cursor[key] && typeof cursor[key] === 'object' ? cursor[key] : {};
      cursor = cursor[key];
    }
  });
  return target;
};

const parseDescriptorValue = (value, field = {}) => {
  if (value === '') return undefined;
  if (field.type === 'boolean') {
    const parsed = optionalBoolean(value);
    return parsed === null ? undefined : parsed;
  }
  if (field.type === 'number') {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (field.type === 'multi_select' || field.type === 'array') {
    return String(value).split(',').map((item) => item.trim()).filter(Boolean);
  }
  if (field.type === 'select') {
    const normalized = String(value).trim().toLocaleLowerCase();
    const match = (field.options || []).find((option) => (
      [option.value, option.code, option.label, option.label_ar]
        .filter((candidate) => candidate !== undefined && candidate !== null)
        .some((candidate) => String(candidate).trim().toLocaleLowerCase() === normalized)
    ));
    return match ? (match.value || match.code) : undefined;
  }
  return value;
};

const MyTeam = () => {
  useUiLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId: routeEmployeeId } = useParams();
  const { currentUser, logout } = useAuth();
  const { t } = useTranslation(['myteam', 'common', 'nationalization']);
  const { isRTL, language } = useLanguage();

  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarState();
  const [rows, setRows] = useState(initialRows);
  const [showImport, setShowImport] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importCountryCode, setImportCountryCode] = useState('');
  const [importEstablishmentId, setImportEstablishmentId] = useState('');
  // Regulatory country descriptors come only from the authenticated backend.
  // Empty state is safer than a stale browser bundle when laws or required fields change.
  const [nationalizationCountries, setNationalizationCountries] = useState([]);
  const [nationalizationProfile, setNationalizationProfile] = useState(null);
  const [jobCodeSuggestions, setJobCodeSuggestions] = useState({});
  const [jobCodesLoading, setJobCodesLoading] = useState(false);
  const [regulatoryImportMode, setRegulatoryImportMode] = useState(false);
  const [regulatoryImportScope, setRegulatoryImportScope] = useState(REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE);
  const [fullWorkforceAttested, setFullWorkforceAttested] = useState(false);
  const [companyUID, setCompanyUID] = useState('');
  const queryRequestedView = new URLSearchParams(location.search).get('view');
  // Nationalization is a first-class page. Keep the old query-string URL only
  // long enough to redirect existing bookmarks to the canonical route.
  const requestedView = location.pathname === '/nationalization'
    ? 'nationalization'
    : queryRequestedView;
  const [activeView, setActiveView] = useState(() => TEAM_VIEWS.has(requestedView) ? requestedView : 'overview');
  const [employeesRaw, setEmployeesRaw] = useState({}); // id -> raw snapshot
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [menuForEmployee, setMenuForEmployee] = useState(null);
  const [agreementGalleryEmployeeId, setAgreementGalleryEmployeeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const sidebarWidth = sidebarCollapsed ? '60px' : '240px';
  const bodyRightPad = sidebarCollapsed ? '100px' : '40px';
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';
  const importUpdateCount = parsedRows.filter((row) => row.importAction === 'update').length;
  const importCreateCount = parsedRows.filter((row) => row.importAction !== 'update').length;
  const eligibleImportEstablishments = useMemo(() => (
    (nationalizationProfile?.establishments || []).filter(
      (establishment) => !establishment.country_code || establishment.country_code === importCountryCode
    )
  ), [importCountryCode, nationalizationProfile]);
  const statusLabels = useMemo(() => ({
    active: t('status.active', 'Active'),
    offboarded: t('status.offboarded', 'Offboarded'),
    onLeave: t('status.onLeave', 'On Leave / PTO'),
    probation: t('status.probation', 'On Probation'),
    onboarding: t('status.onboarding', 'Onboarding')
  }), [t]);
  const alertLabels = useMemo(() => ({
    unpaidPayroll: t('alerts.unpaidPayroll', 'Unpaid Payroll'),
    reviewLeave: t('alerts.reviewLeave', 'Review Leave Request'),
    probationEnding: t('alerts.probationEnding', 'Probation Ending'),
    missingDocuments: t('alerts.missingDocuments', { count: 1, defaultValue: 'Missing {{count}} Document' }),
    upcomingReview: t('alerts.upcomingReview', 'Upcoming Review'),
    requests: t('alerts.requests', { count: 1, defaultValue: '{{count}} Request' })
  }), [t]);
  const teamAccess = useTeamAccessProfile(companyUID);
  const canManageEmployees = teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageEmployees;
  const canViewNationalization = teamAccess.isCompanyOwner
    || teamAccess.userPermissions.canViewNationalization
    || teamAccess.userPermissions.canManageNationalization;
  const canManageNationalization = teamAccess.isCompanyOwner || teamAccess.userPermissions.canManageNationalization;
  const canDeliverLifecycleEvents = teamAccess.isCompanyOwner
    || teamAccess.userPermissions.canManageEmployees
    || teamAccess.userPermissions.canManageOnboarding
    || teamAccess.userPermissions.canManageApplicants
    || teamAccess.userPermissions.canManageNationalization;
  const isEmployeeDetailsPage = Boolean(routeEmployeeId);
  // A query string must never let a nationalization-view-only user open the
  // employee dossier route. Employee details always require employee access.
  const canAccessActiveView = isEmployeeDetailsPage
    ? canManageEmployees
    : activeView === 'nationalization'
      ? canViewNationalization
      : canManageEmployees;
  const accessLoading = teamAccess.loading;

  useEffect(() => {
    setActiveView(TEAM_VIEWS.has(requestedView) ? requestedView : 'overview');
  }, [requestedView]);

  useEffect(() => {
    if (location.pathname === '/my-team' && queryRequestedView === 'nationalization') {
      navigate('/nationalization', { replace: true });
    }
  }, [location.pathname, navigate, queryRequestedView]);

  useEffect(() => {
    // Wait for tenant access to resolve and never let a view-only account mutate
    // the shared outbox's retry state.
    if (!companyUID || accessLoading || !canDeliverLifecycleEvents) return undefined;
    return startNationalizationLifecycleReplay(companyUID);
  }, [accessLoading, canDeliverLifecycleEvents, companyUID]);

  const selectView = (view) => {
    setActiveView(view);
    if (view === 'nationalization') {
      navigate('/nationalization');
      return;
    }
    navigate('/my-team', { replace: true });
  };


  const handleOpenImport = async ({ regulatory = false } = {}) => {
    const includeRegulatoryData = Boolean(regulatory && canManageEmployees && canManageNationalization);
    setRegulatoryImportMode(includeRegulatoryData);
    setParsedRows([]);
    setImportError('');
    setJobCodeSuggestions({});
    setRegulatoryImportScope(REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE);
    setFullWorkforceAttested(false);
    setShowImport(true);

    // Load the regulator-owned form descriptors before a file is selected.
    // Regulatory imports fail closed when this catalogue is unavailable: the
    // browser must never substitute a stale list for country-specific facts.
    if (!includeRegulatoryData) return;
    setNationalizationCountries([]);

    try {
      // The protected facts endpoint accepts only the country saved on the
      // regulatory profile. Loading that profile is therefore mandatory; a
      // company-country guess is never used for a compliance import.
      const profileResult = companyUID
        ? await nationalizationService.getProfile(companyUID)
        : null;
      const countryResult = await nationalizationService.getCountries();
      const nextCountries = Array.isArray(countryResult?.countries)
        ? countryResult.countries
        : [];
      const profile = profileResult?.profile || null;
      const countryCode = profile?.country_code || '';
      if (!countryCode) {
        throw localizedImportError(t('nationalization:import.errors.profileCountryRequired', 'Complete the nationalization company profile before importing regulatory workforce data.'));
      }
      const countryDescriptor = nextCountries.find((country) => country?.code === countryCode);
      if (!countryDescriptor || !Array.isArray(countryDescriptor.employee_fields)) {
        throw localizedImportError(t(
          'nationalization:import.errors.countryDescriptorsRequired',
          'The current regulatory import fields for this country could not be loaded. No file was imported.'
        ));
      }
      setNationalizationCountries(nextCountries);
      setNationalizationProfile(profile);
      setImportCountryCode(countryCode);
      const eligibleEstablishments = (profile?.establishments || []).filter(
        (establishment) => !establishment.country_code || establishment.country_code === countryCode
      );
      setImportEstablishmentId(eligibleEstablishments.length === 1 ? eligibleEstablishments[0].id : '');
    } catch (contextError) {
      console.warn('Nationalization import context could not be loaded:', contextError);
      setNationalizationCountries([]);
      setImportCountryCode('');
      setImportError(contextError?.isLocalizedImportError
        ? contextError.message
        : t('nationalization:import.errors.profileLoadFailed', 'The saved nationalization company profile could not be loaded.'));
    }
  };

  const handleCloseImport = () => {
    setShowImport(false);
    setFullWorkforceAttested(false);
  };

  const parseCSV = (text) => {
    try {
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: (header) => String(header || '').trim().toLowerCase()
      });
      if (shouldRejectCsvParserErrors({
        errors: parsed.errors,
        recordCount: parsed.data?.length,
        regulatory: regulatoryImportMode
      })) {
        console.warn('CSV parser rejected the workforce file:', parsed.errors);
        throw localizedImportError(t('nationalization:import.errors.csvParseFailed', 'The CSV file could not be read. Check the template and try again.'));
      }
      const records = Array.isArray(parsed.data) ? parsed.data : [];
      const hasName = records.some((record) => csvValue(record, ['name', 'employee name', 'full name']));
      if (!regulatoryImportMode && !hasName) {
        throw localizedImportError(t('import.errors.missingName', 'CSV must have a "name" or "employee name" column.'));
      }

      const countryConfig = nationalizationCountries.find((country) => country.code === importCountryCode);
      const countrySpecificFields = Array.isArray(countryConfig?.employee_fields) ? countryConfig.employee_fields : [];

      const existingMatchesByNumber = rows.reduce((lookup, employee) => {
        const number = String(employee.employeeId || '').trim();
        if (!number) return lookup;
        lookup.set(number, [...(lookup.get(number) || []), employee]);
        return lookup;
      }, new Map());
      const csvEmployeeNumbers = new Set();

      const mappedRecords = records.map((record, index) => {
        const roleDepartment = csvValue(record, ['role / department', 'role/department', 'role-department']);
        const roleDepartmentParts = roleDepartment.split('/');
        const role = csvValue(record, ['role', 'job title', 'position', 'role title']) || roleDepartmentParts[0]?.trim() || '';
        const department = csvValue(record, ['department', 'dept']) || roleDepartmentParts[1]?.trim() || '';
        const personalEmail = csvValue(record, ['personal email', 'email']);
        const workEmail = csvValue(record, ['work email', 'company email', 'business email']);
        const salaryValue = csvValue(record, ['salary', 'base salary', 'monthly salary', 'salary amount']);
        const nationalityVerification = csvValue(record, [
          'nationality verification',
          'nationality verified',
          'citizenship verified'
        ]);
        const employeeName = csvValue(record, ['name', 'employee name', 'full name']);
        const employeeNumber = csvValue(record, ['employee id', 'employeeid', 'id', 'emp id', 'employee number']);
        const suppliedEmploymentStatus = csvValue(record, ['status', 'employee status', 'lifecycle status']);
        const missingIdentityFields = regulatoryImportMode
          ? getMissingRegulatoryImportIdentityFields({
            name: employeeName,
            employeeId: employeeNumber,
            employmentStatus: suppliedEmploymentStatus
          })
          : [];
        if (missingIdentityFields.includes('name')) {
          throw localizedImportError(t(
            'nationalization:import.errors.employeeNameRequired',
            'Regulatory row {{row}} (employee {{employeeId}}) needs an employee name. No rows were imported.',
            { row: index + 2, employeeId: employeeNumber || '—' }
          ));
        }
        if (regulatoryImportMode && !employeeNumber) {
          throw localizedImportError(t('nationalization:import.errors.employeeNumberRequired', 'Every regulatory row must include a unique employee ID.'));
        }
        if (regulatoryImportMode && csvEmployeeNumbers.has(employeeNumber)) {
          throw localizedImportError(t('nationalization:import.errors.duplicateEmployeeNumber', 'Employee ID {{employeeId}} appears more than once in this CSV.', {
            employeeId: employeeNumber
          }));
        }
        if (regulatoryImportMode) csvEmployeeNumbers.add(employeeNumber);

        if (regulatoryImportMode && !suppliedEmploymentStatus) {
          throw localizedImportError(t(
            'nationalization:import.errors.employmentStatusRequired',
            'Employee {{employeeId}} needs an explicit employment status before regulatory import.',
            { employeeId: employeeNumber }
          ));
        }
        // The ordinary legacy import keeps its historic active default. A legal
        // import must never turn an omitted status into a current worker.
        const rawEmploymentStatus = suppliedEmploymentStatus || 'active';
        const employmentStatus = normalizeEmploymentStatus(rawEmploymentStatus);
        if (regulatoryImportMode && !NATIONALIZATION_EMPLOYMENT_STATUSES.has(employmentStatus)) {
          throw localizedImportError(t(
            'nationalization:import.errors.unsupportedStatus',
            'Employee {{employeeId}} has an unsupported employment status: {{status}}. Correct it before importing.',
            { employeeId: employeeNumber, status: rawEmploymentStatus }
          ));
        }

        const existingMatches = regulatoryImportMode ? (existingMatchesByNumber.get(employeeNumber) || []) : [];
        if (existingMatches.length > 1) {
          throw localizedImportError(t('nationalization:import.errors.ambiguousEmployeeNumber', 'Employee ID {{employeeId}} matches more than one existing record. Fix the duplicate employee IDs before importing.', {
            employeeId: employeeNumber
          }));
        }
        const matchedEmployee = existingMatches[0] || null;
        // Reserve the final Firebase key before AI classification. Regulatory
        // re-imports reuse the one exact employee-number match; unmatched rows
        // receive a stable key that is reused if the protected facts call fails.
        const pendingEmployeeKey = matchedEmployee?.id || (companyUID
          ? push(ref(database, `companies/${companyUID}/employees`)).key
          : `${Date.now()}_${index}`);
        const rowCountryCode = normalizeCountryCode(csvValue(record, ['country code', 'country']) || importCountryCode);
        if (regulatoryImportMode && rowCountryCode !== importCountryCode) {
          throw localizedImportError(t('nationalization:import.errors.countryMismatch', 'Employee {{employeeId}} uses country {{country}}, but this profile is locked to {{profileCountry}}.', {
            employeeId: employeeNumber,
            country: rowCountryCode,
            profileCountry: importCountryCode
          }));
        }
        const suppliedEstablishmentId = csvValue(record, ['establishment id', 'legal establishment id']);
        const eligibleEstablishments = (nationalizationProfile?.establishments || []).filter(
          (establishment) => !establishment.country_code || establishment.country_code === importCountryCode
        );
        const rowEstablishmentId = resolveRegulatoryImportEstablishmentId({
          suppliedEstablishmentId,
          eligibleEstablishments
        });
        if (regulatoryImportMode && eligibleEstablishments.length > 1 && !suppliedEstablishmentId) {
          throw localizedImportError(t(
            'nationalization:import.errors.establishmentRequiredPerRow',
            'Employee {{employeeId}} needs an establishment ID because this company has more than one legal establishment.',
            { employeeId: employeeNumber }
          ));
        }
        const validEstablishmentIds = new Set(eligibleEstablishments.map((item) => item.id));
        if (regulatoryImportMode && rowEstablishmentId && !validEstablishmentIds.has(rowEstablishmentId)) {
          throw localizedImportError(t('nationalization:import.errors.establishmentMismatch', 'Employee {{employeeId}} uses an establishment that is not in the saved company profile.', {
            employeeId: employeeNumber
          }));
        }

        const regulatoryExtras = {};
        countrySpecificFields.filter((field) => String(field.key || '').startsWith('custom_facts.')).forEach((field) => {
          const key = field.key;
          const header = String(field.csv_header || key || '').replace(/_/g, ' ').toLowerCase();
          const rawValue = csvValue(record, [header, String(key || '').toLowerCase()]);
          const parsedValue = parseDescriptorValue(rawValue, field);
          if (rawValue !== '' && parsedValue === undefined) {
            throw localizedImportError(t(
              'nationalization:import.errors.invalidCountryField',
              'Employee {{employeeId}} has an invalid value for {{field}}.',
              { employeeId: employeeNumber, field: field.label || field.key }
            ));
          }
          if (parsedValue !== undefined) setObjectPath(regulatoryExtras, key, parsedValue);
        });
        const sourceReference = csvValue(record, ['source reference', 'regulatory source reference']);
        const verifiedAt = csvValue(record, ['verified at', 'facts verified at']);
        if (regulatoryImportMode && (!sourceReference || !isValidEvidenceDate(verifiedAt, importCountryCode))) {
          throw localizedImportError(t('nationalization:import.errors.factEvidenceRequired', 'Employee {{employeeId}} needs a source reference and a real, non-future verified-at date in YYYY-MM-DD format.', {
            employeeId: employeeNumber
          }));
        }

        return {
          id: pendingEmployeeKey,
          pendingEmployeeKey,
          importAction: matchedEmployee ? 'update' : 'create',
          name: employeeName,
          role,
          department,
          startDate: csvValue(record, ['start date', 'start', 'hire date', 'joining date']),
          endDate: csvValue(record, ['end date', 'termination date', 'last working date']),
          personalEmail,
          workEmail,
          email: personalEmail || workEmail,
          phone: csvValue(record, ['phone', 'phone number', 'mobile', 'contact']),
          employeeId: employeeNumber,
          managerId: csvValue(record, ['manager id', 'managerid', 'manager']),
          managerName: csvValue(record, ['manager name', 'manager_name']),
          employmentType: csvValue(record, ['employment type', 'type', 'contract type']) || 'full_time',
          status: employmentStatus,
          duties: csvValue(record, ['duties', 'responsibilities', 'job duties']),
          qualifications: csvValue(record, ['qualifications', 'education', 'certifications']),
          countryCode: rowCountryCode,
          establishmentId: rowEstablishmentId,
          branch: csvValue(record, ['branch', 'branch name']),
          worksite: csvValue(record, ['worksite', 'work site', 'work location']),
          nationality: csvValue(record, ['nationality']),
          citizenship: csvValue(record, ['citizenship']),
          nationalityVerificationStatus: nationalityVerification || 'unverified',
          nationalityVerified: optionalBoolean(nationalityVerification),
          officialOccupationCode: csvValue(record, ['official occupation code', 'government job code', 'occupation code']),
          officialOccupationLabel: csvValue(record, ['official occupation label', 'government job title']),
          officialOccupationGroup: csvValue(record, ['official occupation group', 'occupation group']),
          occupationMappingConfirmed: optionalBoolean(csvValue(record, [
            'occupation mapping confirmed',
            'job code confirmed'
          ])),
          skillLevel: csvValue(record, ['occupation skill level', 'skill level', 'official skill level']),
          salaryAmount: salaryValue === '' || !Number.isFinite(Number(salaryValue)) ? null : Number(salaryValue),
          salaryCurrency: csvValue(record, ['salary currency', 'currency']),
          contractDocumented: optionalBoolean(csvValue(record, ['contract documented', 'official contract documented'])),
          socialInsuranceRegistered: optionalBoolean(csvValue(record, ['social insurance registered', 'insurance registered'])),
          workPermitValid: optionalBoolean(csvValue(record, ['work permit valid', 'valid work permit'])),
          pensionRegistered: optionalBoolean(csvValue(record, ['pension registered', 'pension fund registered'])),
          wpsCompliant: optionalBoolean(csvValue(record, ['wps compliant', 'wps registered', 'wps paid'])),
          accreditationValid: optionalBoolean(csvValue(record, ['accreditation valid', 'professional accreditation valid'])),
          accreditationStatus: csvValue(record, ['accreditation status', 'professional licence status', 'professional license status']),
          qualificationAttested: optionalBoolean(csvValue(record, ['qualification attested', 'qualification verified and attested'])),
          sourceReference,
          verifiedAt,
          regulatoryExtras
        };
      });
      // Ordinary imports retain their historical tolerance for blank-name rows.
      // Regulatory imports are all-or-nothing and have already rejected any such row.
      return regulatoryImportMode ? mappedRecords : mappedRecords.filter((row) => row.name);
    } catch (e) {
      console.error('CSV parse error:', e);
      throw e?.isLocalizedImportError
        ? e
        : localizedImportError(t('nationalization:import.errors.csvParseFailed', 'The CSV file could not be read. Check the template and try again.'));
    }
  };

  const loadJobCodeSuggestions = async (nextRows, countryCode) => {
    const candidates = nextRows.filter((row) => (
      row.role && !row.officialOccupationCode && !row.officialOccupationGroup
    ));
    if (!companyUID || !countryCode || !candidates.length) return;
    setJobCodesLoading(true);

    // Limit concurrency so a large workforce file does not overload the AI
    // classification endpoint or the employer's browser.
    for (let index = 0; index < candidates.length; index += 3) {
      const batch = candidates.slice(index, index + 3);
      await Promise.all(batch.map(async (row) => {
        setJobCodeSuggestions((current) => ({
          ...current,
          [row.id]: { loading: true, suggestions: [], error: '' }
        }));
        try {
          const result = await nationalizationService.suggestJobCodes(companyUID, {
            employeeId: row.pendingEmployeeKey,
            countryCode: row.countryCode || countryCode,
            title: row.role,
            duties: row.duties,
            qualifications: row.qualifications
          });
          setJobCodeSuggestions((current) => ({
            ...current,
            [row.id]: {
              loading: false,
              suggestions: Array.isArray(result?.suggestions) ? result.suggestions : [],
              suggestionRunId: result?.suggestion_run_id || '',
              error: ''
            }
          }));
        } catch (suggestionError) {
          setJobCodeSuggestions((current) => ({
            ...current,
            [row.id]: {
              loading: false,
              suggestions: [],
              error: suggestionError?.message || 'Job-code suggestions unavailable'
            }
          }));
        }
      }));
    }
    setJobCodesLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // A new file invalidates any earlier completeness declaration. The employer
    // must attest the exact file that is about to be submitted.
    setFullWorkforceAttested(false);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = String(ev.target?.result || '');
        const nextRows = parseCSV(text);
        setParsedRows(nextRows);
        setImportError(nextRows.length === 0 ? t('import.errors.noRows', 'No rows found. Ensure CSV has name, role, start date.') : '');
        if (regulatoryImportMode) loadJobCodeSuggestions(nextRows, importCountryCode).catch((suggestionError) => {
          console.warn('Job-code suggestions could not be loaded:', suggestionError);
          setJobCodesLoading(false);
        });
      } catch (parseError) {
        setParsedRows([]);
        setJobCodeSuggestions({});
        setJobCodesLoading(false);
        setImportError(parseError?.message || t('nationalization:import.errors.csvParseFailed', 'The CSV file could not be read. Check the template and try again.'));
      }
    };
    reader.readAsText(file);
  };

  const selectJobCodeSuggestion = (rowId, selectionValue) => {
    const rowSuggestions = jobCodeSuggestions[rowId] || {};
    const suggestion = (rowSuggestions.suggestions || []).find(
      (item) => occupationSuggestionValue(item) === selectionValue
    );
    setParsedRows((current) => current.map((row) => row.id === rowId ? {
      ...row,
      officialOccupationCode: suggestion?.code || suggestion?.occupation_code || '',
      officialOccupationGroup: suggestion?.occupation_group || suggestion?.group || '',
      officialOccupationLabel: suggestion?.label || '',
      officialOccupationLabelAr: suggestion?.label_ar || '',
      jobCodeSelected: Boolean(suggestion),
      jobCodeConfidence: suggestion?.confidence ?? null,
      jobCodeSuggestionRunId: rowSuggestions.suggestionRunId || ''
    } : row));
  };

  const handleImportRows = async () => {
    if (!canManageEmployees) {
      setImportError(t('import.errors.noAccess', 'You do not have permission to import employee records.'));
      return;
    }
    if (regulatoryImportMode && !canManageNationalization) {
      setImportError(t('nationalization:import.errors.noRegulatoryAccess', 'You do not have permission to import regulatory workforce information.'));
      return;
    }
    if (!companyUID) {
      setImportError(t('import.errors.noCompany', 'Company not identified; please re-login.'));
      return;
    }
    if (regulatoryImportMode && !importCountryCode) {
      setImportError(t('nationalization:import.errors.countryRequired', 'Select the workforce country before importing.'));
      return;
    }
    if (
      regulatoryImportMode
      && regulatoryImportScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE
      && !fullWorkforceAttested
    ) {
      setImportError(t(
        'nationalization:import.errors.fullWorkforceAttestationRequired',
        'Confirm that this file contains the complete current workforce before importing it as a full workforce file.'
      ));
      return;
    }
    if (parsedRows.length === 0) return;

    let rawEmployeeWriteCompleted = false;
    try {
      const baseRef = ref(database, `companies/${companyUID}/employees`);
      const confirmedMappings = [];
      const regulatoryFactRows = [];
      const employeeWrites = {};
      const communitySyncPayloads = [];
      const importedEmployeeKeys = [];

      for (const r of parsedRows) {
        const newRef = r.pendingEmployeeKey
          ? ref(database, `companies/${companyUID}/employees/${r.pendingEmployeeKey}`)
          : push(baseRef);
        const employeeKey = newRef.key;
        importedEmployeeKeys.push(employeeKey);

        // Parse role and department
        const roleStr = r.role || '';
        let role = roleStr;
        let department = r.department || '';
        if (!department && roleStr.includes('/')) {
          const [a, b] = roleStr.split('/');
          role = (a || '').trim();
          department = (b || '').trim();
        } else {
          role = (role || '').trim();
          department = (department || '').trim();
        }

        // Resolve manager
        let managerId = r.managerId || '';
        let managerName = r.managerName || '';
        if (!managerId && managerName) {
          // Try to match by name from already imported employees
          const match = rows.find(x => (x.name || '').toLowerCase() === managerName.toLowerCase());
          if (match) managerId = match.id;
        }

        // Determine status
        const status = normalizeEmploymentStatus(r.status || 'active');
        const finalStatus = NATIONALIZATION_EMPLOYMENT_STATUSES.has(status) ? status : 'active';

        // Build comprehensive employee record using the utility
        const employeeRecord = buildEmployeeRecordFromHire({
          employeeId: employeeKey,
          companyUID,
          createdBy: currentUser?.uid,
          createdByEmail: currentUser?.email,
          status: finalStatus,
          source: 'csv_import',
          existingEmployee: employeesRaw[employeeKey] || {},
          hire: {
            name: r.name,
            personalEmail: r.personalEmail || r.email,
            workEmail: r.workEmail || '',
            phone: r.phone,
            roleTitle: role,
            role: role,
            department: department,
            startDate: r.startDate,
            endDate: r.endDate,
            managerUID: managerId,
            managerName: managerName,
            employeeNumber: r.employeeId,
            employmentType: r.employmentType || 'full_time'
          },
        });

        // The employee roster is widely readable by people-operations roles.
        // Nationality, legal wage, official occupation and eligibility evidence
        // therefore skip this object entirely and travel only to the protected
        // backend facts endpoint assembled below.

        // Add legacy compatibility fields for existing code
        const roleDepartment = [role, department].filter(Boolean).join(' / ');
        if (roleDepartment) {
          employeeRecord.roleDepartment = roleDepartment;
        }
        if (managerId) {
          employeeRecord.managerId = managerId;
        }
        if (managerName) {
          employeeRecord.managerName = managerName;
        }

        // Accumulate the entire workforce change for one atomic RTDB update.
        // This prevents a browser/network interruption halfway through the CSV
        // from leaving only the first portion of the workforce saved.
        employeeWrites[`companies/${companyUID}/employees/${employeeKey}`] = employeeRecord;

        if (!regulatoryImportMode) {
          // Ordinary CSV imports can change active status/start dates and job roles.
          // Attach one minimized marker per employee to this same root write so the
          // backend invalidates stale signed lifecycle/occupation facts. Regulatory
          // imports use their separate protected import-session handshake instead.
          addNationalizationLifecycleEventToUpdates(employeeWrites, {
            companyUID,
            employeeId: employeeKey,
            source: 'csv_import',
            changeKinds: ['lifecycle', 'occupation']
          });
        }

        if (regulatoryImportMode) {
          regulatoryFactRows.push({
            employee_id: employeeKey,
            source_reference: r.sourceReference,
            verified_at: r.verifiedAt,
            facts: omitUnknownRegulatoryFacts({
              country_code: r.countryCode || importCountryCode,
              establishment_id: r.establishmentId || importEstablishmentId,
              nationality: r.nationality || '',
              nationality_verified: r.nationalityVerified,
              employment_status: finalStatus,
              start_date: ['onboarding', 'preboarding', 'hired', 'accepted', 'offer_accepted', 'pending_start'].includes(finalStatus)
                ? ''
                : r.startDate || '',
              end_date: r.endDate || '',
              planned_start_date: ['onboarding', 'preboarding', 'hired', 'accepted', 'offer_accepted', 'pending_start'].includes(finalStatus)
                ? r.startDate || ''
                : '',
              occupation_skill_level: r.skillLevel || '',
              monthly_salary: r.salaryAmount,
              salary_currency: r.salaryCurrency || '',
              contract_documented: r.contractDocumented,
              social_insurance_registered: r.socialInsuranceRegistered,
              work_permit_valid: r.workPermitValid,
              wps_compliant: r.wpsCompliant,
              pension_registered: r.pensionRegistered,
              accreditation_valid: r.accreditationValid,
              qualification_attested: r.qualificationAttested,
              ...(r.regulatoryExtras?.custom_facts ? {
                custom_facts: r.regulatoryExtras.custom_facts
              } : {}),
              ...(r.officialOccupationCode || r.officialOccupationGroup ? {
                occupation_mapping: {
                  occupation_code: r.officialOccupationCode,
                  occupation_group: r.officialOccupationGroup || '',
                  is_group: !r.officialOccupationCode && Boolean(r.officialOccupationGroup),
                  label: r.officialOccupationLabel || '',
                  label_ar: r.officialOccupationLabelAr || '',
                  suggestion_run_id: r.jobCodeSuggestionRunId || '',
                  source: r.jobCodeSelected ? 'ai_suggestion' : 'employer_import',
                  confirmed: false
                }
              } : {})
            })
          });
        }

        if (
          regulatoryImportMode
          && (r.jobCodeSelected || r.occupationMappingConfirmed === true)
          && (r.officialOccupationCode || r.officialOccupationGroup)
        ) {
          confirmedMappings.push({
            employeeId: employeeKey,
            mapping: {
              countryCode: r.countryCode || importCountryCode,
              code: r.officialOccupationCode || '',
              occupationGroup: r.officialOccupationGroup || '',
              isGroup: !r.officialOccupationCode && Boolean(r.officialOccupationGroup),
              label: r.officialOccupationLabel,
              labelAr: r.officialOccupationLabelAr,
              suggestionRunId: r.jobCodeSuggestionRunId
            }
          });
        }

        try {
          const connectionKey = employeeRecord?.connectionKey || '';
          const importedProfileId = connectionKey.includes('_imported_')
            ? connectionKey.split('_imported_')[1]?.split('_copy_')[0]
            : null;
          const applicantId = employeeRecord?.applicantId || null;
          const personUid = importedProfileId ? null : applicantId;
          const workforceEmail = (
            employeeRecord?.contact?.workEmail
            || employeeRecord?.contact?.email
            || employeeRecord?.contact?.personalEmail
            || ''
          ).trim();

          communitySyncPayloads.push({
            member_id: personUid
              ? `person_${personUid}`
              : (importedProfileId ? `imported_${importedProfileId}` : `employee_${employeeKey}`),
            member_type: 'employee_or_onboarding',
            person_uid: personUid,
            imported_profile_id: importedProfileId,
            employee_id: employeeKey,
            applicant_id: applicantId,
            connection_key: connectionKey || null,
            email: workforceEmail,
            channels: {
              workforce: true,
            },
          });
        } catch (syncError) {
          console.warn('Community workforce sync failed for imported employee row:', syncError);
        }
      }

      let regulatoryImportContext = null;
      if (regulatoryImportMode) {
        // Canonical employee ordering makes each 1,000-row chunk an exact slice of
        // the sorted expected ID list; overlaps and gaps cannot hide in CSV order.
        const regulatoryFactChunks = buildCanonicalRegulatoryImportChunks(regulatoryFactRows, 1000);
        const expectedEmployeeIds = normalizeRegulatoryImportEmployeeIds(importedEmployeeKeys);
        const expectedEmployeeIdsHash = hashRegulatoryImportEmployeeIds(expectedEmployeeIds);
        const factsBindingToken = createRegulatoryImportFactsBindingToken();
        const expectedEmployeeCount = expectedEmployeeIds.length;
        const expectedChunkCount = regulatoryFactChunks.length;
        const importScope = regulatoryImportScope;
        const scopeAttested = importScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE
          && fullWorkforceAttested;
        // Each user submission gets a new opaque session. HTTP retries inside the
        // service reuse this in-memory ID/token/body, but corrected facts can never
        // collide with chunks from an older attempt.
        const importId = allocateRegulatoryImportId(companyUID);
        const queuedAt = Date.now();

        expectedEmployeeIds.forEach((employeeId) => {
          const employeePath = `companies/${companyUID}/employees/${employeeId}`;
          const employeeRecord = employeeWrites[employeePath];
          if (employeeRecord && typeof employeeRecord === 'object') {
            // This revision binds the raw roster row to the protected import session.
            employeeRecord.nationalizationImportRevision = importId;
          }
        });
        addRegulatoryImportMarkerToUpdates(employeeWrites, {
          companyUID,
          importId,
          expectedEmployeeIdsHash,
          factsBindingToken,
          importScope,
          fullWorkforceAttested: scopeAttested,
          expectedEmployeeCount,
          expectedChunkCount,
          queuedAt
        });
        regulatoryImportContext = {
          importId,
          factsBindingToken,
          importScope,
          fullWorkforceAttested: scopeAttested,
          expectedEmployeeIds,
          expectedChunkCount,
          regulatoryFactChunks
        };
      }

      await update(ref(database), employeeWrites);
      rawEmployeeWriteCompleted = true;

      if (!regulatoryImportMode) {
        let lifecycleFlush = null;
        try {
          lifecycleFlush = await nationalizationService.flushLifecycleOutbox(companyUID);
        } catch (syncError) {
          // The per-row markers remain durable for online/visibility replay.
          console.warn('Nationalization CSV lifecycle sync is pending:', syncError);
        }
        announceNationalizationWorkforceChange({
          companyUID,
          source: 'csv_import',
          recalculated: Number(lifecycleFlush?.delivered || 0) > 0,
          syncPending: !lifecycleFlush || Number(lifecycleFlush.pending || 0) > 0
        });
      }

      await Promise.all(communitySyncPayloads.map((payload) => (
        syncCommunityMember(currentUser, payload).catch((syncError) => {
          console.warn('Community workforce sync failed for imported employee row:', syncError);
        })
      )));

      if (regulatoryImportMode) {
        // Browser writes are never authoritative for legal facts. The backend
        // validates every row against the saved country/establishment profile,
        // then records the signed fact audit used by the calculation engine.
        // The API intentionally caps one request at 1,000 rows. Chunking supports
        // large employers without weakening that backend payload limit.
        let importCompleted = false;
        for (let chunkIndex = 0; chunkIndex < regulatoryImportContext.regulatoryFactChunks.length; chunkIndex += 1) {
          const finalChunk = chunkIndex === regulatoryImportContext.expectedChunkCount - 1;
          const importResult = await nationalizationService.saveEmployeeFacts(
            companyUID,
            regulatoryImportContext.regulatoryFactChunks[chunkIndex],
            {
              importId: regulatoryImportContext.importId,
              factsBindingToken: regulatoryImportContext.factsBindingToken,
              importScope: regulatoryImportContext.importScope,
              fullWorkforceAttested: regulatoryImportContext.fullWorkforceAttested,
              expectedEmployeeIds: regulatoryImportContext.expectedEmployeeIds,
              expectedChunkCount: regulatoryImportContext.expectedChunkCount,
              chunkIndex,
              finalChunk
            }
          );
          if (finalChunk) importCompleted = importResult?.import_completed === true;
        }
        if (!importCompleted) {
          throw new Error('The backend did not confirm the full regulatory import.');
        }
        // The backend removes this marker on completion. Duplicate client cleanup
        // covers an interrupted cleanup response without weakening the handshake.
        await removeRegulatoryImportMarker(companyUID, regulatoryImportContext.importId);
      }

      // Confirm employer-selected AI mappings only after every employee record
      // has a stable Firebase employee ID and protected facts record.
      for (let offset = 0; offset < confirmedMappings.length; offset += 25) {
        await Promise.all(confirmedMappings.slice(offset, offset + 25).map(({ employeeId, mapping }) => (
          nationalizationService.confirmJobCode(companyUID, employeeId, mapping)
        )));
      }

      try {
        await triggerCommunityAutoSync(currentUser);
      } catch (syncError) {
        console.warn('Community auto-sync failed after team import:', syncError);
      }

      if (regulatoryImportMode) {
        let recalculated = false;
        try {
          await nationalizationService.recalculate(companyUID, { forecastDays: 90 });
          recalculated = true;
        } catch (calculationError) {
          // Import success must not be rolled back merely because a follow-up
          // calculation is temporarily unavailable.
          console.warn('Nationalization recalculation failed after workforce import:', calculationError);
        }
        announceNationalizationWorkforceChange({ companyUID, source: 'csv_import', recalculated });
      }

      setShowImport(false);
      setParsedRows([]);
      setJobCodeSuggestions({});
      setImportError('');
    } catch (e) {
      console.error('Import error:', e);
      if (regulatoryImportMode && rawEmployeeWriteCompleted) {
        setImportError(t('nationalization:import.errors.protectedFactsFailed', 'Employee records were saved, but their protected regulatory facts were not accepted. Keep this window open, review the required fields, and re-import the same employee IDs; they will be updated rather than duplicated.'));
        return;
      }
      if (regulatoryImportMode) {
        setImportError(t('nationalization:import.errors.importFailed', 'The regulatory workforce import could not be completed. Review the template and try again.'));
        return;
      }
      setImportError(t('import.errors.failed', {
        message: e.message || t('import.errors.unknown', 'Unknown error'),
        defaultValue: `Failed to import: ${e.message || 'Unknown error'}`
      }));
    }
  };

  const downloadTemplate = () => {
    const employeeHeaders = [
      'name',
      'employee id',
      'personal email',
      'work email',
      'phone',
      'role',
      'department',
      'manager id',
      'start date',
      'employment type',
      'status'
    ];
    const regulatoryHeaders = [
      'country code',
      'establishment id',
      'branch',
      'worksite',
      'nationality',
      'citizenship',
      'nationality verified',
      'occupation code',
      'official occupation label',
      'occupation group',
      'occupation mapping confirmed',
      'occupation skill level',
      'monthly salary',
      'salary currency',
      'contract documented',
      'social insurance registered',
      'work permit valid',
      'pension registered',
      'wps compliant',
      'accreditation valid',
      'accreditation status',
      'qualification attested',
      'source reference',
      'verified at',
      'duties',
      'qualifications'
    ];
    const countryConfig = nationalizationCountries.find((country) => country.code === importCountryCode);
    const extraHeaders = (countryConfig?.employee_fields || [])
      .filter((field) => String(field.key || '').startsWith('custom_facts.'))
      .map((field) => String(field.csv_header || field.key || '').replace(/_/g, ' ').toLowerCase())
      .filter((header) => header && !regulatoryHeaders.includes(header));
    const headers = regulatoryImportMode
      ? Array.from(new Set([...employeeHeaders, ...regulatoryHeaders, ...extraHeaders]))
      : employeeHeaders;
    const sample = Object.fromEntries(headers.map((header) => [header, '']));
    Object.assign(sample, {
      name: 'Jane Doe',
      'employee id': 'E-1001',
      'work email': 'jane.doe@company.com',
      role: 'Software Engineer',
      department: 'Engineering',
      'start date': '2026-01-15',
      'employment type': 'full_time',
      status: 'active',
      'country code': importCountryCode,
      'establishment id': importEstablishmentId,
      nationality: '',
      'nationality verified': '',
      'salary currency': importCountryCode === 'SA' ? 'SAR' : importCountryCode === 'AE' ? 'AED' : '',
      'source reference': '',
      'verified at': '',
      'contract documented': '',
      'occupation mapping confirmed': 'no',
      duties: 'Builds and maintains production software systems.'
    });
    if (importCountryCode === 'SA') {
      // Saudi rows must state how Qiwa/Nitaqat counts each Saudi employee.
      // The value is ignored for non-Saudi workers, but keeping the column in
      // the sample makes the required Saudi workflow discoverable.
      sample['nitaqat employment type'] = 'standard';
    }
    const csv = Papa.unparse([sample], { columns: headers });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Resolve company UID (owner or collaborator) and subscribe to employees
  useEffect(() => {
    const resolveCompany = async () => {
      try {
        if (!currentUser) return;
        const ownerSnap = await get(ref(database, `companies/${currentUser.uid}`));
        if (ownerSnap.exists()) {
          setCompanyUID(currentUser.uid);
          return;
        }
        const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
        if (collabSnap.exists()) {
          const c = collabSnap.val();
          // Support both collaborator schemas used by the shared access layer.
          const collaboratorCompanyUID = c?.companyId || c?.company_uid;
          if (collaboratorCompanyUID) setCompanyUID(collaboratorCompanyUID);
        }
      } catch (e) {
        console.error('Company resolution error:', e);
      }
    };
    resolveCompany();
  }, [currentUser]);

  useEffect(() => {
    if (!companyUID || accessLoading || !canManageEmployees) {
      setRows([]);
      setEmployeesRaw({});
      return undefined;
    }
    const empRef = ref(database, `companies/${companyUID}/employees`);
    const unsub = onValue(empRef, (snap) => {
      if (!snap.exists()) {
        setRows([]);
        setEmployeesRaw({});
        return;
      }
      const data = snap.val();
      setEmployeesRaw(data || {});
      const listRaw = Object.entries(data).map(([id, v]) => {
        const profile = v.profile || {};
        const contact = v.contact || {};
        const lifecycle = v.lifecycle || {};
        const roleCombined = v.roleDepartment || '';
        const role =
          profile.roleTitle ||
          v.role ||
          (roleCombined.includes('/') ? roleCombined.split('/')[0]?.trim() : roleCombined) ||
          '';
        const department =
          profile.department ||
          v.department ||
          (roleCombined.includes('/') ? roleCombined.split('/')[1]?.trim() : '') ||
          '';
        const employeeId = v.employeeId || v.employeeID || v.empId || v.meta?.employeeNumber || '';
        const probation = Boolean(
          profile?.probation?.inProbation ||
          v?.probation === true ||
          v?.probation?.active === true ||
          v?.onboarding?.probation?.active === true
        );
        const managerId = profile.managerUID || v.managerId || v.managerID || '';
        const base = {
          id,
          name: profile.name || v.name || '',
          photoUrl: profile.photoUrl || profile.photoURL || v.photoUrl || v.photoURL || '',
          role,
          department,
          startDate: profile.startDate || v.startDate || '',
          workEmail: profile.workEmail || contact.workEmail || '',
          email: profile.email || contact.personalEmail || contact.email || '',
          phone: profile.phone || contact.personalPhone || contact.phone || '',
          employeeId,
          probation,
          managerId,
          managerName: v.managerName || profile.managerName || '',
          status: lifecycle.status || v.status || '',
        };
        return base;
      });
      // Resolve manager names if managerId refers to another employee
      const idToName = Object.fromEntries(
        Object.entries(data).map(([id, v]) => [id, (v.profile?.name || v.name || '')])
      );
      const list = listRaw.map(r => ({
        ...r,
        managerName: r.managerName || (r.managerId ? (idToName[r.managerId] || '') : '')
      }));
      setRows(list);
    }, (err) => console.error('Employees listener error:', err));
    return () => unsub();
  }, [accessLoading, canManageEmployees, companyUID]);

  // Derived: filtered + paginated rows
  const scopedRows = teamAccess.filterByDepartmentAccess(rows, (row) => row.department);
  const filteredRows = scopedRows.filter(r => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const departmentLocalized = translateDepartment(r.department, language);
    const hay = [r.name, r.employeeId, departmentLocalized, r.role].map(x => (x || '').toString().toLowerCase());
    return hay.some(x => x.includes(q));
  });
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageRows = filteredRows.slice(start, end);

  // Helpers
  const openRowMenu = (e, row) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 8, left: rect.right - 220 });
    setMenuForEmployee(row);
    setMenuOpenFor(row.id);
  };

  const closeRowMenu = () => {
    setMenuOpenFor(null);
    setMenuForEmployee(null);
  };

  // Modal management for actions
  const [actionModal, setActionModal] = useState(null); // { type, employeeId }
  const [reviewModal, setReviewModal] = useState(null); // { employeeId, requests: [] }
  const openAction = (type, employeeId) => setActionModal({ type, employeeId });
  const closeAction = () => setActionModal(null);
  const openContactDetails = (employeeId) => navigate(`/my-team/employee/${employeeId}`);
  const closeContactDetails = () => navigate('/my-team');
  const showEmployeeNotFound = isEmployeeDetailsPage && Object.keys(employeesRaw).length > 0 && !employeesRaw[routeEmployeeId];

  const accessDeniedView = (
    <div className="team-table-wrapper" style={{ padding: '32px' }}>
      <div className="name-text" style={{ fontSize: '20px', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '10px' }}>
        {activeView === 'nationalization'
          ? t('nationalization:errors.forbiddenTitle', 'Nationalization access is restricted')
          : t('access.employeeRestrictedTitle', 'Employee record access is restricted')}
      </div>
      <div className="status-text" style={{ maxWidth: '720px', fontSize: '14px', lineHeight: 1.7 }}>
        {activeView === 'nationalization'
          ? t('nationalization:errors.forbidden', 'Ask a workspace administrator to grant nationalization access to your account.')
          : t('access.employeeRestricted', 'Your team profile does not include employee management access. Ask a workspace administrator to enable employee record access for your account.')}
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div
        className="home-body"
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          marginInlineStart: sidebarWidth,
          transition: 'all 250ms ease',
          width: `calc(100vw - ${sidebarWidth})`,
          boxSizing: 'border-box',
          paddingTop: '50px',
          paddingBottom: '0px',
          paddingInlineStart: '40px',
          paddingInlineEnd: bodyRightPad,
          gap: '0',
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          height: '100vh'
        }}
      >
        <FeatureGate showBanner={false}>
          {accessLoading ? (
            <AppLoading
              inline
              label={activeView === 'nationalization'
                ? t('nationalization:common.loading', 'Loading...')
                : t('loading', 'Loading team...')}
            />
          ) : !canAccessActiveView ? accessDeniedView : (
            <>
          {isEmployeeDetailsPage ? (
            showEmployeeNotFound ? (
              <div
                className="team-table-wrapper"
                style={{
                  minHeight: 'calc(100vh - 160px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px'
                }}
              >
                <div style={{ textAlign: 'center', maxWidth: 560 }}>
                  <button
                    className="post-job-cta"
                    onClick={closeContactDetails}
                    style={{ marginBottom: 20 }}
                  >
                    <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`}></i>
                    <span>{t('employeePage.back', 'Back to My Team')}</span>
                  </button>
                  <div className="name-text" style={{ fontSize: '20px', fontWeight: "var(--app-weight-strong, 700)", marginBottom: '10px' }}>
                    {t('employeePage.notFound', 'Employee not found')}
                  </div>
                </div>
              </div>
            ) : (
              <EmployeePage
                employeeId={routeEmployeeId}
                employeesRaw={employeesRaw}
                companyUID={companyUID}
                onClose={closeContactDetails}
                onBack={closeContactDetails}
                fullPage
              />
            )
          ) : (
            <>
          {activeView !== 'nationalization' && (
            <>
          <div className="team-toolbar">
            <h2 className="manage-title" style={{ margin: 0 }}>{t('title', 'My team')}</h2>
            <div className="team-search">
              <i className="fa-solid fa-magnifying-glass" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}></i>
              <input
                type="text"
                placeholder={t('searchPlaceholder', 'Search by name, ID, department, or role')}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              />
            </div>
            <button className="post-job-cta secondary" onClick={() => navigate('/payroll')}><i className="fa-solid fa-money-check-dollar" aria-hidden="true" /><span>{uiText('Run payroll')}</span></button>
            <button className="post-job-cta" style={{ whiteSpace: 'nowrap' }} onClick={() => handleOpenImport({ regulatory: false })}>
              <i className="fa-solid fa-circle-plus"></i>
              <span>{t('importEmployees', 'Import Employees')}</span>
            </button>
          </div>

          {/* Top toggles (exact chip style from Home analytics) */}
          <div className="team-controls">
            <button className={`chip ${activeView === 'overview' ? 'chip-active' : ''}`} onClick={() => selectView('overview')}>
              <i className="fa-solid fa-table-list"></i> <span style={{ fontSize: '16px' }}>{t('views.overview', 'Overview')}</span>
            </button>
            <button className={`chip ${activeView === 'pto' ? 'chip-active' : ''}`} onClick={() => selectView('pto')}>
              <i className="fa-solid fa-plane-departure"></i> <span style={{ fontSize: '16px' }}>{t('views.pto', 'PTO / Time Off')}</span>
            </button>
            <button className={`chip ${activeView === 'compensation' ? 'chip-active' : ''}`} onClick={() => selectView('compensation')}>
              <i className="fa-solid fa-money-bill-wave"></i> <span style={{ fontSize: '16px' }}>{t('views.compensation', 'Compensation')}</span>
            </button>
            <button className={`chip ${activeView === 'performance' ? 'chip-active' : ''}`} onClick={() => selectView('performance')}>
              <i className="fa-solid fa-chart-line"></i> <span style={{ fontSize: '16px' }}>{t('views.performance', 'Performance')}</span>
            </button>
            <button className={`chip ${activeView === 'agreements' ? 'chip-active' : ''}`} onClick={() => selectView('agreements')}>
              <i className="fa-solid fa-file-contract"></i> <span style={{ fontSize: '16px' }}>{t('views.agreements', 'Agreements')}</span>
            </button>
          </div>
            </>
          )}

          {/* Table / Content area */}
          {activeView === 'overview' ? (
            <div className="team-table-wrapper">
              <div className="team-table-scroll">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user"></i>
                          <span>{t('table.employeeName', 'Employee Name')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-briefcase"></i>
                          <span>{t('table.role', 'Role')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-building"></i>
                          <span>{t('table.department', 'Department')}</span>
                        </div>
                      </th>
                      <th style={{ width: '18%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-triangle-exclamation"></i>
                          <span>{t('table.actionAlerts', 'Action Alerts')}</span>
                        </div>
                      </th>
                      <th style={{ width: '16%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user-tie"></i>
                          <span>{t('table.manager', 'Manager')}</span>
                        </div>
                      </th>
                      <th style={{ width: '16%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calendar-days"></i>
                          <span>{t('table.lastUpdate', 'Last Update')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row) => {
                      const empData = employeesRaw[row.id] || {};
                      const lifecycle = empData.lifecycle || {};
                      const profile = empData.profile || {};

                      // Determine employee status
                      let employeeStatus = statusLabels.active;
                      let statusColor = '#40EB6E';
                      const lifecycleStatus = (lifecycle.status || empData.status || '').toLowerCase();

                      if (lifecycleStatus === 'offboarded' || lifecycleStatus === 'terminated') {
                        employeeStatus = statusLabels.offboarded;
                        statusColor = '#EB4040';
                      } else if (lifecycleStatus === 'on_leave' || lifecycleStatus === 'leave') {
                        employeeStatus = statusLabels.onLeave;
                        statusColor = '#CC40EB';
                      } else if (row.probation || profile?.probation?.inProbation) {
                        employeeStatus = statusLabels.probation;
                        statusColor = '#EB8D40';
                      } else if (lifecycleStatus === 'onboarding') {
                        employeeStatus = statusLabels.onboarding;
                        statusColor = '#EBDD40';
                      }

                      // Generate comprehensive action alerts based on employee data
                      const actionAlerts = [];
                      const alerts = lifecycle.alerts || {};
                      const documents = empData.documents || {};
                      const performance = empData.performance || {};
                      const compensation = empData.compensation || {};
                      const pto = empData.pto || {};
                      const now = Date.now();

                      // 1. UNPAID PAYROLL - Check compensation status and pay schedule
                      const lastPaid = compensation.lastPaidAt ? new Date(compensation.lastPaidAt) : null;
                      const isPaidThisMonth = lastPaid &&
                        lastPaid.getMonth() === new Date().getMonth() &&
                        lastPaid.getFullYear() === new Date().getFullYear();

                      // If compensation status is 'pending' or payroll is marked as unpaid OR NOT PAID THIS MONTH
                      if (alerts.payrollUnpaid === true || compensation.status === 'pending' || !isPaidThisMonth) {
                        actionAlerts.push({ type: alertLabels.unpaidPayroll, color: '#EB4040' });
                      }
                      // Additional check: if paySchedule exists but no recent payslips
                      else if (compensation.paySchedule && compensation.payslips) {
                        const payslipDates = Object.keys(compensation.payslips).map(d => new Date(d).getTime());
                        const lastPayslip = payslipDates.length > 0 ? Math.max(...payslipDates) : 0;
                        const daysSinceLastPay = (now - lastPayslip) / (1000 * 60 * 60 * 24);
                        // If no pay in last 45 days and employee is active
                        if (daysSinceLastPay > 45 && lifecycleStatus !== 'onboarding') {
                          actionAlerts.push({ type: alertLabels.unpaidPayroll, color: '#EB4040' });
                        }
                      }

                      // 2. REVIEW LEAVE REQUEST - Check for pending PTO requests
                      const ptoRequests = Object.values(pto.requests || {});
                      const pendingLeaveRequests = ptoRequests.filter(req => req.status === 'pending');
                      if (alerts.leavePending === true || pendingLeaveRequests.length > 0) {
                        actionAlerts.push({ type: alertLabels.reviewLeave, color: '#CC40EB' });
                      }

                      // 3. PROBATION ENDING - Check if probation end date is within 30 days
                      if (profile?.probation?.inProbation && profile.probation.endDate) {
                        try {
                          const endDate = new Date(profile.probation.endDate);
                          const daysUntilEnd = Math.floor((endDate.getTime() - now) / (1000 * 60 * 60 * 24));
                          if (daysUntilEnd >= 0 && daysUntilEnd <= 30) {
                            actionAlerts.push({ type: alertLabels.probationEnding, color: '#EBDD40' });
                          }
                        } catch (e) {
                          // Invalid date format, skip
                        }
                      }

                      // 4. MISSING DOCUMENTS - Check required vs provided documents
                      let missingDocsCount = alerts.docsMissing || documents.missingCount || 0;

                      // Calculate missing docs from required vs files
                      if (documents.required && Array.isArray(documents.required) && documents.files) {
                        const requiredDocs = documents.required;
                        const providedDocs = Object.keys(documents.files || {});
                        const actualMissing = requiredDocs.filter(req => !providedDocs.includes(req));
                        missingDocsCount = Math.max(missingDocsCount, actualMissing.length);
                      }

                      // For onboarding employees, assume some documents are missing if none specified
                      if (missingDocsCount === 0 && lifecycleStatus === 'onboarding') {
                        const hasAnyDocs = documents.files && Object.keys(documents.files).length > 0;
                        if (!hasAnyDocs) {
                          missingDocsCount = 3; // Assume 3 standard docs needed
                        }
                      }

                      if (missingDocsCount > 0) {
                        actionAlerts.push({
                          type: t('alerts.missingDocuments', {
                            count: missingDocsCount,
                            defaultValue: `Missing ${missingDocsCount} Document${missingDocsCount > 1 ? 's' : ''}`
                          }),
                          color: '#EB8D40'
                        });
                      }

                      // 5. UPCOMING REVIEW - Check performance review dates
                      if (alerts.reviewDue === true) {
                        actionAlerts.push({ type: alertLabels.upcomingReview, color: '#4048EB' });
                      } else if (performance.nextReviewDate) {
                        try {
                          const reviewDate = new Date(performance.nextReviewDate);
                          const daysUntilReview = Math.floor((reviewDate.getTime() - now) / (1000 * 60 * 60 * 24));
                          if (daysUntilReview >= 0 && daysUntilReview <= 30) {
                            actionAlerts.push({ type: alertLabels.upcomingReview, color: '#4048EB' });
                          }
                        } catch (e) {
                          // Invalid date format, skip
                        }
                      }
                      // For employees with no review date set but employed > 90 days
                      else if (!performance.nextReviewDate && empData.profile?.startDate) {
                        try {
                          const startDate = new Date(empData.profile.startDate);
                          const daysSinceStart = (now - startDate.getTime()) / (1000 * 60 * 60 * 24);
                          if (daysSinceStart > 90 && lifecycleStatus === 'active') {
                            actionAlerts.push({ type: alertLabels.upcomingReview, color: '#4048EB' });
                          }
                        } catch (e) {
                          // Invalid start date, skip
                        }
                      }

                      // 6. MANAGER REQUESTS
                      const requests = empData.requests || {};
                      const pendingRequests = Object.entries(requests)
                        .map(([key, val]) => ({ id: key, ...val }))
                        .filter(r => r.status === 'pending_hr');
                      if (pendingRequests.length > 0) {
                        actionAlerts.push({
                          type: t('alerts.requests', {
                            count: pendingRequests.length,
                            defaultValue: `${pendingRequests.length} Request${pendingRequests.length > 1 ? 's' : ''}`
                          }),
                          color: '#568ded',
                          action: 'reviewRequests',
                          data: pendingRequests
                        });
                      }

                      // Last update date - from meta.updatedAt
                      const meta = empData.meta || {};
                      const lastUpdate = meta.updatedAt || empData.lastModified || meta.createdAt || empData.createdAt;
                      const formattedLastUpdate = lastUpdate
                        ? new Date(typeof lastUpdate === 'number' ? lastUpdate : lastUpdate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';
                      const displayDepartment = translateDepartment(row.department, language);

                      return (
                        <tr key={row.id}>
                          <td>
                            <div className="employee-name-cell">
                              <button
                                className="employee-name-button"
                                onClick={() => openContactDetails(row.id)}
                              >
                                {row.name}
                              </button>
                              <div className="employee-status">
                                <span className="status-dot" style={{ backgroundColor: statusColor }}></span>
                                <span className="status-text">{employeeStatus}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="cell-text">{row.role || '—'}</span>
                          </td>
                          <td>
                            <span className="cell-text">{displayDepartment || '—'}</span>
                          </td>
                          <td>
                            <div className="action-alerts-cell">
                              {actionAlerts.length > 0 ? actionAlerts.map((alert, idx) => (
                                <button
                                  key={idx}
                                  className="action-alert-button"
                                  style={{
                                    backgroundColor: `${alert.color}4D`, // 30% opacity (4D in hex)
                                    border: `1px solid ${alert.color}26` // ~15% opacity (26 in hex)
                                  }}
                                  onClick={() => {
                                    if (alert.action === 'reviewRequests') {
                                      setReviewModal({ employeeId: row.id, requests: alert.data, employeeName: row.name });
                                    } else {
                                      // Handle alert click - will implement actions later
                                      console.log('Alert clicked:', alert.type, 'for employee:', row.name);
                                    }
                                  }}
                                >
                                  <span
                                    className="alert-indicator"
                                    style={{ backgroundColor: alert.color }} // Full opacity
                                  ></span>
                                  <span className="alert-text">{alert.type}</span>
                                </button>
                              )) : (
                                <span className="cell-text" style={{ opacity: 0.5 }}>—</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {(() => {
                              // Read manager from profile.managerUID or managerId
                              const managerId = profile?.managerUID || empData.managerId || row.managerId;
                              const managerName = empData.managerName || row.managerName;

                              if (managerName && managerId) {
                                return (
                                  <button
                                    className="manager-name-button"
                                    onClick={() => openContactDetails(managerId)}
                                  >
                                    {managerName}
                                  </button>
                                );
                              } else if (managerName) {
                                return <span className="cell-text">{managerName}</span>;
                              } else {
                                return <span className="cell-text">—</span>;
                              }
                            })()}
                          </td>
                          <td>
                            <span className="cell-text">{formattedLastUpdate}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination controls: outside scroll, sticky bottom */}
              <div className="team-pagination">
                <div className="left">
                  <span className="cell-text" style={{ opacity: 0.8 }}>{t('pagination.rowsPerPage', 'Rows per page:')}</span>
                  <select className="per-page-select" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="cell-text" style={{ opacity: 0.7 }}>{t('pagination.total', { count: filteredRows.length, defaultValue: `${filteredRows.length} total` })}</span>
                </div>
                <div className="right">
                  <button className="pager-button" disabled={safePage <= 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>{t('pagination.prev', 'Prev')}</button>
                  <span className="cell-text" style={{ opacity: 0.8 }}>{t('pagination.pageXofY', { current: safePage, total: totalPages, defaultValue: `Page ${safePage} of ${totalPages}` })}</span>
                  <button className="pager-button" disabled={safePage >= totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>{t('pagination.next', 'Next')}</button>
                </div>
              </div>
            </div>
          ) : activeView === 'pto' ? (
            // PTO / Time Off View
            <div className="team-table-wrapper">
              <div className="team-table-scroll">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user"></i>
                          <span>{t('pto.columns.employee', 'Employee Name')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calendar-check"></i>
                          <span>{t('pto.columns.annual', 'Annual PTO')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-heart-pulse"></i>
                          <span>{t('pto.columns.sick', 'Sick Leave')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-clock"></i>
                          <span>{t('pto.columns.unpaid', 'Unpaid Leave')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-hourglass-half"></i>
                          <span>{t('pto.columns.pending', 'Pending Requests')}</span>
                        </div>
                      </th>
                      <th style={{ width: '20%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calendar-arrow-down"></i>
                          <span>{t('pto.columns.nextReturn', 'Next Return Date')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const empData = employeesRaw[row.id] || {};
                      const pto = empData.pto || {};
                      const balances = pto.balances || {};
                      const annual = balances.annual || { entitled: 0, used: 0, remaining: 0 };
                      const sick = balances.sick || { entitled: 0, used: 0, remaining: 0 };
                      const unpaid = balances.unpaid || { used: 0 };
                      const requests = Object.values(pto.requests || {});
                      const pendingRequests = requests.filter(r => r.status === 'pending');
                      const nextReturn = pto.nextReturnDate
                        ? new Date(pto.nextReturnDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';

                      return (
                        <tr key={row.id}>
                          <td>
                            <button
                              className="employee-name-button"
                              onClick={() => openContactDetails(row.id)}
                            >
                              {row.name}
                            </button>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span className="cell-text">{t('pto.daysLeft', { count: annual.remaining, defaultValue: `${annual.remaining} days left` })}</span>
                              <span className="status-text">{t('pto.usedEntitled', { used: annual.used, entitled: annual.entitled, defaultValue: `${annual.used} used / ${annual.entitled} entitled` })}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <span className="cell-text">{t('pto.daysLeft', { count: sick.remaining, defaultValue: `${sick.remaining} days left` })}</span>
                              <span className="status-text">{t('pto.usedEntitled', { used: sick.used, entitled: sick.entitled, defaultValue: `${sick.used} used / ${sick.entitled} entitled` })}</span>
                            </div>
                          </td>
                          <td>
                            <span className="cell-text">{t('pto.unpaidUsed', { count: unpaid.used, defaultValue: `${unpaid.used} days used` })}</span>
                          </td>
                          <td>
                            {pendingRequests.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {pendingRequests.map((req, idx) => (
                                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span className="cell-text">{t(`pto.types.${req.type || 'vacation'}`, req.type || 'vacation')}</span>
                                    <span className="status-text">{t('pto.requestDays', { count: req.days || 0, defaultValue: `${req.days || 0} days` })}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            <span className="cell-text">{nextReturn}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeView === 'compensation' ? (
            // Compensation View
            <div className="team-table-wrapper">
              <div className="team-table-scroll">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th style={{ width: '18%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user"></i>
                          <span>{t('comp.columns.employee', 'Employee Name')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-dollar-sign"></i>
                          <span>{t('comp.columns.base', 'Base Salary')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-plus"></i>
                          <span>{t('comp.columns.allowances', 'Allowances')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-coins"></i>
                          <span>{t('comp.columns.bonuses', 'Bonuses')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-minus"></i>
                          <span>{t('comp.columns.deductions', 'Deductions')}</span>
                        </div>
                      </th>
                      <th style={{ width: '13%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calculator"></i>
                          <span>{t('comp.columns.net', 'Net Total')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calendar-days"></i>
                          <span>{t('comp.columns.schedule', 'Pay Schedule')}</span>
                        </div>
                      </th>
                      <th style={{ width: '15%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-circle-check"></i>
                          <span>{t('comp.columns.status', 'Payroll Status')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const empData = employeesRaw[row.id] || {};
                      const compensation = empData.compensation || {};
                      const base = compensation.base || 0;
                      const allowances = compensation.allowances || [];
                      const deductions = compensation.deductions || [];
                      const bonuses = compensation.bonuses || {};
                      const totals = compensation.totals;
                      const schedule = compensation.paySchedule || '—';

                      // Derived Status
                      const lastPaid = compensation.lastPaidAt ? new Date(compensation.lastPaidAt) : null;
                      const isPaidThisMonth = lastPaid &&
                        lastPaid.getMonth() === new Date().getMonth() &&
                        lastPaid.getFullYear() === new Date().getFullYear();

                      const status = isPaidThisMonth ? 'paid' : 'pending';

                      const totalAllowances = allowances.reduce((sum, a) => sum + (a.amount || 0), 0);
                      const totalDeductions = deductions.reduce((sum, d) => sum + (d.amount || 0), 0);

                      // Calculate total bonuses for this month
                      const now = new Date();
                      const totalBonuses = Object.values(bonuses).reduce((sum, b) => {
                        const d = new Date(b.date);
                        if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
                          return sum + (b.amount || 0);
                        }
                        return sum;
                      }, 0);

                      const netTotal = totals || (base + totalAllowances + totalBonuses - totalDeductions);

                      const statusColor = status === 'paid' ? '#40EB6E' : status === 'pending' ? '#EBDD40' : '#EB4040';

                      return (
                        <tr key={row.id}>
                          <td>
                            <button
                              className="employee-name-button"
                              onClick={() => openContactDetails(row.id)}
                            >
                              {row.name}
                            </button>
                          </td>
                          <td>
                            <span className="cell-text">
                              {typeof base === 'number' ? `$${base.toLocaleString()}` : base}
                            </span>
                          </td>
                          <td>
                            {allowances.length > 0 ? (
                              <span className="cell-text" style={{ color: 'var(--app-tone-40eb6e, #40EB6E)' }}>
                                +${totalAllowances.toLocaleString()}
                              </span>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            {totalBonuses > 0 ? (
                              <span className="cell-text" style={{ color: 'var(--app-tone-40eb6e, #40EB6E)' }}>
                                +${totalBonuses.toLocaleString()}
                              </span>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            {deductions.length > 0 ? (
                              <span className="cell-text" style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>
                                -${totalDeductions.toLocaleString()}
                              </span>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            <span className="cell-text" style={{ fontWeight: 600 }}>
                              {netTotal ? `$${netTotal.toLocaleString()}` : '—'}
                            </span>
                          </td>
                          <td>
                            <span className="cell-text">{schedule}</span>
                          </td>
                          <td>
                            <div className="employee-status">
                              <span className="status-dot" style={{ backgroundColor: statusColor }}></span>
                              <span className="status-text" style={{ textTransform: 'capitalize' }}>{t(`comp.status.${status}`, status)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeView === 'performance' ? (
            // Performance View
            <div className="team-table-wrapper">
              <div className="team-table-scroll">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th style={{ width: '16%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user"></i>
                          <span>{t('performance.columns.employee', 'Employee Name')}</span>
                        </div>
                      </th>
                      <th style={{ width: '10%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-star"></i>
                          <span>{t('performance.columns.currentRating', 'Current Rating')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-user-check"></i>
                          <span>{t('performance.columns.qualityBeta', 'Quality Beta')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-gauge-high"></i>
                          <span>{t('performance.columns.rampStatus', 'Ramp Status')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-clipboard-check"></i>
                          <span>{t('performance.columns.probation', 'Probation')}</span>
                        </div>
                      </th>
                      <th style={{ width: '12%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-calendar-check"></i>
                          <span>{t('performance.columns.nextReview', 'Next Review Date')}</span>
                        </div>
                      </th>
                      <th style={{ width: '9%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-file-lines"></i>
                          <span>{t('performance.columns.totalReviews', 'Total Reviews')}</span>
                        </div>
                      </th>
                      <th style={{ width: '9%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-bullseye"></i>
                          <span>{t('performance.columns.activeGoals', 'Active Goals')}</span>
                        </div>
                      </th>
                      <th style={{ width: '8%' }}>
                        <div className="table-header">
                          <i className="fa-solid fa-comments"></i>
                          <span>{t('performance.columns.feedback', 'Feedback Count')}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const empData = employeesRaw[row.id] || {};
                      const performance = empData.performance || {};
                      const qualityOfHire = empData.qualityOfHire || {};
                      const profile = empData.profile || {};
                      const probation = profile.probation || empData.probation || {};
                      const currentRating = performance.currentRating;
                      const qualityScore = qualityOfHire.betaScore;
                      const rampStatus = qualityOfHire.rampStatus || 'not_started';
                      const probationStatus = probation.status || (probation.inProbation ? 'in_progress' : 'not_applicable');
                      const nextReview = performance.nextReviewDate
                        ? new Date(performance.nextReviewDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—';
                      const reviews = Object.values(performance.reviews || {});
                      const goals = Object.values(performance.goals || {});
                      const activeGoals = goals.filter(g => g.status !== 'completed' && g.status !== 'cancelled');
                      const feedback = Object.values(performance.feedback || {});

                      const ratingColor = currentRating >= 4 ? '#40EB6E' : currentRating >= 3 ? '#EBDD40' : currentRating >= 2 ? '#EB8D40' : '#EB4040';
                      const qualityColor = Number(qualityScore) >= 80 ? '#40EB6E' : Number(qualityScore) >= 60 ? '#EBDD40' : qualityScore !== undefined && qualityScore !== null && qualityScore !== '' ? '#EB8D40' : 'rgba(255,255,255,0.55)';

                      return (
                        <tr key={row.id}>
                          <td>
                            <button
                              className="employee-name-button"
                              onClick={() => openContactDetails(row.id)}
                            >
                              {row.name}
                            </button>
                          </td>
                          <td>
                            {currentRating ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="cell-text" style={{ color: ratingColor, fontWeight: 600 }}>
                                  {currentRating.toFixed(1)}
                                </span>
                                <span className="status-text">/ 5.0</span>
                              </div>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            {qualityScore !== undefined && qualityScore !== null && qualityScore !== '' ? (
                              <span className="cell-text" style={{ color: qualityColor, fontWeight: 600 }}>{Number(qualityScore).toFixed(0)} / 100</span>
                            ) : (
                              <span className="cell-text">—</span>
                            )}
                          </td>
                          <td>
                            <span className="cell-text" style={{ textTransform: 'capitalize' }}>{uiText(String(rampStatus).replace(/_/g, ' '))}</span>
                          </td>
                          <td>
                            <span className="cell-text" style={{ textTransform: 'capitalize' }}>{uiText(String(probationStatus).replace(/_/g, ' '))}</span>
                          </td>
                          <td>
                            <span className="cell-text">{nextReview}</span>
                          </td>
                          <td>
                            <span className="cell-text">{reviews.length}</span>
                          </td>
                          <td>
                            <span className="cell-text">{activeGoals.length}</span>
                          </td>
                          <td>
                            <span className="cell-text">{feedback.length}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeView === 'nationalization' ? (
            <NationalizationView
              companyUID={companyUID}
              language={language}
              canManage={canManageNationalization}
              onOpenImport={canManageEmployees && canManageNationalization
                ? () => handleOpenImport({ regulatory: true })
                : null}
            />
          ) : activeView === 'agreements' ? (
            // Agreements View
            <div className={`team-table-wrapper ${agreementGalleryEmployeeId ? 'no-border' : ''}`}>
              <div className="team-table-scroll">
                {!agreementGalleryEmployeeId ? (
                  <table className="team-table">
                    <thead>
                      <tr>
                        <th style={{ width: '25%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-user"></i>
                            <span>{t('agreements.columns.employee', 'Employee Name')}</span>
                          </div>
                        </th>
                        <th style={{ width: '15%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-check-circle"></i>
                            <span>{t('agreements.columns.signed', 'Signed')}</span>
                          </div>
                        </th>
                        <th style={{ width: '15%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-clock"></i>
                            <span>{t('agreements.columns.pending', 'Pending')}</span>
                          </div>
                        </th>
                        <th style={{ width: '15%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-ban"></i>
                            <span>{t('agreements.columns.obsolete', 'Obsolete')}</span>
                          </div>
                        </th>
                        <th style={{ width: '15%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-file-lines"></i>
                            <span>{t('agreements.columns.total', 'Total Docs')}</span>
                          </div>
                        </th>
                        <th style={{ width: '15%' }}>
                          <div className="table-header">
                            <i className="fa-solid fa-eye"></i>
                            <span>{t('agreements.columns.actions', 'Actions')}</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => {
                        const empData = employeesRaw[row.id] || {};
                        const agreements = Object.values(empData.agreements || []);
                        const signed = agreements.filter(a => a.status === 'signed').length;
                        const pending = agreements.filter(a => a.status === 'pending').length;
                        const obsolete = agreements.filter(a => a.status === 'obsolete').length;
                        const total = agreements.length;

                        return (
                          <tr key={row.id}>
                            <td>
                              <button
                                className="employee-name-button"
                                onClick={() => setAgreementGalleryEmployeeId(row.id)}
                              >
                                {row.name}
                              </button>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="status-dot" style={{ backgroundColor: '#40EB6E' }}></span>
                                <span className="cell-text">{signed}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="status-dot" style={{ backgroundColor: '#EBDD40' }}></span>
                                <span className="cell-text">{pending}</span>
                              </div>
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="status-dot" style={{ backgroundColor: '#9E9E9E' }}></span>
                                <span className="cell-text">{obsolete}</span>
                              </div>
                            </td>
                            <td>
                              <span className="cell-text" style={{ fontWeight: 600 }}>{total}</span>
                            </td>
                            <td>
                              <button
                                className="post-job-cta"
                                onClick={() => setAgreementGalleryEmployeeId(row.id)}
                                style={{ fontSize: '14px', padding: '6px 12px' }}
                              >
                                <i className="fa-solid fa-eye"></i>
                                <span>{t('agreements.view', 'View')}</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <AgreementsGallery
                    employeeId={agreementGalleryEmployeeId}
                    employeesRaw={employeesRaw}
                    companyUID={companyUID}
                    onBack={() => setAgreementGalleryEmployeeId(null)}
                  />
                )}
              </div>
            </div>
          ) : null}

          {/* Import Employees Modal */}
          {showImport && (
            <div className="modal-overlay" onClick={handleCloseImport}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{t('import.title', 'Import Employees')}</h2>
                  <div className="close" onClick={handleCloseImport}>&times;</div>
                </div>
                <div className="form-section" style={{ paddingTop: '16px' }}>
                  {regulatoryImportMode && <div className="nationalization-import-context">
                    <label>
                      <span>{t('nationalization:import.country', 'Workforce country')}</span>
                      <select
                        value={importCountryCode}
                        onChange={(event) => {
                          const countryCode = event.target.value;
                          const matchingEstablishments = (nationalizationProfile?.establishments || []).filter(
                            (establishment) => establishment.country_code === countryCode
                          );
                          setImportCountryCode(countryCode);
                          setImportEstablishmentId(
                            matchingEstablishments.length === 1 ? matchingEstablishments[0].id : ''
                          );
                          setParsedRows([]);
                          setJobCodeSuggestions({});
                          setFullWorkforceAttested(false);
                        }}
                        disabled={Boolean(nationalizationProfile?.country_code)}
                      >
                        <option value="">{t('nationalization:common.selectCountry', 'Select a country')}</option>
                        {nationalizationCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {language === 'ar' ? (country.label_ar || country.label) : country.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>{importCountryCode === 'SA'
                        ? t('nationalization:import.qiwaEntity', 'Qiwa entity')
                        : t('nationalization:import.establishment', 'Legal company / branch')}</span>
                      {eligibleImportEstablishments.length === 1 ? (
                        <>
                          <input
                            className="nationalization-import-readonly"
                            value={eligibleImportEstablishments[0].name || eligibleImportEstablishments[0].id}
                            readOnly
                            aria-readonly="true"
                          />
                          <small className="nationalization-import-establishment-note">
                            {t('nationalization:import.singleEstablishmentDefault', 'This establishment is the default when a CSV row leaves establishment ID empty.')}
                          </small>
                        </>
                      ) : eligibleImportEstablishments.length > 1 ? (
                        <p className="nationalization-import-establishment-note is-warning">
                          {t('nationalization:import.establishmentPerRowNote', 'This profile has multiple legal establishments. Every CSV row must include a valid establishment ID.')}
                        </p>
                      ) : (
                        <p className="nationalization-import-establishment-note is-warning">
                          {t('nationalization:import.noEstablishment', 'Not linked yet')}
                        </p>
                      )}
                    </label>
                    <div className="nationalization-import-scope" role="group" aria-labelledby="nationalization-import-scope-label">
                      <span id="nationalization-import-scope-label" className="nationalization-import-scope-title">
                        {t('nationalization:import.scope.label', 'How much of the workforce is in this file?')}
                      </span>
                      <div className="nationalization-import-scope-options">
                        <label className="nationalization-import-scope-option">
                          <input
                            type="radio"
                            name="nationalization-import-scope"
                            value={REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE}
                            checked={regulatoryImportScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE}
                            onChange={() => {
                              setRegulatoryImportScope(REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE);
                              setFullWorkforceAttested(false);
                            }}
                          />
                          <span>
                            <strong>{t('nationalization:import.scope.fullWorkforce', 'Complete current workforce')}</strong>
                            <small>{t('nationalization:import.scope.fullWorkforceHelp', 'Use this only when every current employee is included.')}</small>
                          </span>
                        </label>
                        <label className="nationalization-import-scope-option">
                          <input
                            type="radio"
                            name="nationalization-import-scope"
                            value={REGULATORY_IMPORT_SCOPES.PARTIAL}
                            checked={regulatoryImportScope === REGULATORY_IMPORT_SCOPES.PARTIAL}
                            onChange={() => {
                              setRegulatoryImportScope(REGULATORY_IMPORT_SCOPES.PARTIAL);
                              setFullWorkforceAttested(false);
                            }}
                          />
                          <span>
                            <strong>{t('nationalization:import.scope.partial', 'Partial workforce update')}</strong>
                            <small>{t('nationalization:import.scope.partialHelp', 'Updates these employees only; company-wide quotas stay provisional.')}</small>
                          </span>
                        </label>
                      </div>
                      {regulatoryImportScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE ? (
                        <label className="nationalization-import-attestation">
                          <input
                            type="checkbox"
                            checked={fullWorkforceAttested}
                            onChange={(event) => setFullWorkforceAttested(event.target.checked)}
                          />
                          <span>{t('nationalization:import.scope.attestation', 'I confirm this file contains the company’s complete current workforce, including nationals and non-nationals across all branches in this country.')}</span>
                        </label>
                      ) : (
                        <p className="nationalization-import-scope-warning">
                          {t('nationalization:import.scope.partialWarning', 'JOBSEEQR will keep the overall and legally applicable breakdown results provisional until a complete current workforce file is verified.')}
                        </p>
                      )}
                    </div>
                  </div>}
                  <p style={{ color: 'var(--app-heading, #031128)', marginBottom: '10px', fontSize: '16px' }}>
                    {regulatoryImportMode
                      ? (importCountryCode === 'SA'
                        ? t('nationalization:import.instructionsSA', 'Upload the Saudi workforce CSV. For each Saudi employee, include the Nitaqat employment type and the matching Qiwa/GOSI facts so JOBSEEQR can apply the correct employee weight.')
                        : t('nationalization:import.instructions', 'Upload the workforce CSV. The template includes the verified facts needed for a reliable calculation.'))
                      : t('import.instructions', 'Upload a CSV with columns: name (required), employee id, personal email, work email, phone, role, department, manager id, start date, employment type, status')}
                  </p>
                  <p style={{ color: '#666', marginBottom: '10px', fontSize: '14px' }}>
                    {regulatoryImportMode
                      ? t('nationalization:import.privacyNote', 'Leave unknown values empty. JOBSEEQR never guesses nationality, citizenship or legal eligibility.')
                      : t('import.validValues', 'Valid statuses: active, onboarding, on_leave, offboarded. Employment types: full_time, part_time, contract, intern.')}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                    <input type="file" accept=".csv" onChange={handleFileChange} disabled={regulatoryImportMode && !importCountryCode} />
                    <button className="btn-secondary" onClick={downloadTemplate} disabled={regulatoryImportMode && !importCountryCode} style={{ fontSize: '16px' }}>
                      <i className="fa-solid fa-download"></i> {t('import.downloadTemplate', 'Download Template')}
                    </button>
                  </div>
                  {jobCodesLoading && (
                    <p className="nationalization-import-ai-status">
                      <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                      {t('nationalization:import.jobMatching.loading', 'Suggesting official job codes...')}
                    </p>
                  )}
                  {regulatoryImportMode && parsedRows.length > 0 && (
                    <p className="nationalization-import-summary">
                      {t('nationalization:import.upsertSummary', '{{updates}} existing employees will be updated; {{creates}} new employees will be created.', {
                        updates: importUpdateCount,
                        creates: importCreateCount
                      })}
                    </p>
                  )}
                  {importError && (
                    <p style={{ color: 'var(--app-tone-c0392b, #c0392b)', marginTop: '10px' }}>{importError}</p>
                  )}
                  {parsedRows.length > 0 && (
                    <div className="team-table-wrapper" style={{ marginTop: '16px' }}>
                      <table className="team-table">
                        <thead>
                          <tr>
                            <th style={{ width: '18%' }}>{t('import.preview.name', 'Name')}</th>
                            <th style={{ width: '18%' }}>{t('import.preview.roleDepartment', 'Role / Department')}</th>
                            {regulatoryImportMode ? (
                              <>
                                <th style={{ width: '18%' }}>{t('nationalization:import.jobMatching.officialCode', 'Official job code')}</th>
                                <th style={{ width: '12%' }}>{t('nationalization:import.fields.nationality', 'Nationality')}</th>
                                <th style={{ width: '12%' }}>{t('nationalization:import.fields.branchWorksite', 'Branch / worksite')}</th>
                                <th style={{ width: '10%' }}>{t('nationalization:import.fields.salary', 'Salary')}</th>
                              </>
                            ) : (
                              <>
                                <th style={{ width: '12%' }}>{t('import.preview.employeeId', 'Employee ID')}</th>
                                <th style={{ width: '16%' }}>{t('import.preview.manager', 'Manager')}</th>
                                <th style={{ width: '16%' }}>{t('import.preview.contact', 'Contact')}</th>
                              </>
                            )}
                            <th style={{ width: '12%' }}>{t('import.preview.startDate', 'Start Date')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedRows.slice(0, 8).map((r) => (
                            <tr key={r.id}>
                              {regulatoryImportMode ? <><td>
                                <div className="name-cell">
                                  <LetterBadge name={r.name} photoUrl={r.photoUrl} />
                                  <span className="name-text">
                                    {r.name}
                                    <small className={`nationalization-import-action ${r.importAction}`}>
                                      {r.importAction === 'update'
                                        ? t('nationalization:import.actions.update', 'Update')
                                        : t('nationalization:import.actions.create', 'Create')}
                                    </small>
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className="cell-text">{r.role}</span>
                                {r.department && <span className="micro-subtext">{translateDepartment(r.department, language)}</span>}
                              </td>
                              <td>
                                {(r.officialOccupationCode || r.officialOccupationGroup) && !jobCodeSuggestions[r.id]?.suggestions?.length ? (
                                  <>
                                    <span className="cell-text">{r.officialOccupationCode || r.officialOccupationGroup}</span>
                                    {r.officialOccupationLabel && <span className="micro-subtext">{r.officialOccupationLabel}</span>}
                                  </>
                                ) : jobCodeSuggestions[r.id]?.loading ? (
                                  <span className="micro-subtext">{t('nationalization:import.jobMatching.loadingShort', 'Matching...')}</span>
                                ) : jobCodeSuggestions[r.id]?.suggestions?.length ? (
                                  <>
                                    <select
                                      className="nationalization-job-code-select"
                                      value={r.officialOccupationCode
                                        ? `code:${r.officialOccupationCode}`
                                        : r.officialOccupationGroup
                                          ? `group:${r.officialOccupationGroup}`
                                          : ''}
                                      onChange={(event) => selectJobCodeSuggestion(r.id, event.target.value)}
                                    >
                                      <option value="">{t('nationalization:import.jobMatching.confirmPrompt', 'Confirm a suggested code')}</option>
                                      {jobCodeSuggestions[r.id].suggestions.map((suggestion) => (
                                        <option key={occupationSuggestionValue(suggestion)} value={occupationSuggestionValue(suggestion)}>
                                          {suggestion.code || suggestion.occupation_code || suggestion.occupation_group || suggestion.group} · {language === 'ar' ? (suggestion.label_ar || suggestion.label) : suggestion.label} ({Math.round(Number(suggestion.confidence || 0) * 100)}%)
                                        </option>
                                      ))}
                                    </select>
                                    <span className="micro-subtext">{t('nationalization:import.jobMatching.employerMustConfirm', 'AI suggestion — employer confirmation required')}</span>
                                  </>
                                ) : (
                                  <span className="micro-subtext">{t('nationalization:import.jobMatching.unavailable', 'No confirmed code')}</span>
                                )}
                              </td>
                              <td>
                                <span className="cell-text">{r.nationality || r.citizenship || '—'}</span>
                                <span className="micro-subtext">
                                  {t(`nationalization:import.verificationStatus.${r.nationalityVerificationStatus || 'unverified'}`, r.nationalityVerificationStatus || t('nationalization:common.unverified', 'Unverified'))}
                                </span>
                              </td>
                              <td>
                                <span className="cell-text">{r.branch || '—'}</span>
                                {r.worksite && <span className="micro-subtext">{r.worksite}</span>}
                              </td>
                              <td><span className="cell-text">{r.salaryAmount === null ? '—' : `${r.salaryAmount} ${r.salaryCurrency || ''}`}</span></td></> : <>
                                <td><span className="cell-text">{r.employeeId || '—'}</span></td>
                                <td><span className="cell-text">{r.managerName || r.managerId || '—'}</span></td>
                                <td><span className="cell-text">{[r.email, r.phone].filter(Boolean).join(' · ')}</span></td>
                              </>}
                              <td><span className="cell-text">{r.startDate}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={handleCloseImport}>{t('common:buttons.cancel', 'Cancel')}</button>
                  <button
                    className="btn-primary"
                    disabled={
                      parsedRows.length === 0
                      || (regulatoryImportMode && !importCountryCode)
                      || (
                        regulatoryImportMode
                        && regulatoryImportScope === REGULATORY_IMPORT_SCOPES.FULL_WORKFORCE
                        && !fullWorkforceAttested
                      )
                      || jobCodesLoading
                    }
                    onClick={handleImportRows}
                  >
                    {t('import.submit', 'Import')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Review Requests Modal */}
          {reviewModal && (
            <ReviewRequestModal
              companyUID={companyUID}
              employeeId={reviewModal.employeeId}
              employeeName={reviewModal.employeeName}
              requests={reviewModal.requests}
              onClose={() => setReviewModal(null)}
            />
          )}

          {/* Action Modals */}
          {actionModal && (
            <EmployeeActionModal
              type={actionModal.type}
              employeeId={actionModal.employeeId}
              employeesRaw={employeesRaw}
              companyUID={companyUID}
              onClose={closeAction}
            />
          )}
          {/* Agreements gallery is inline inside Agreements view */}
            </>
          )}
            </>
          )}
        </FeatureGate>
      </div>
    </div>
  );
};

// Unified modal for row actions and view operations
const EmployeeActionModal = ({ type, employeeId, employeesRaw, companyUID, onClose }) => {
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL, language } = useLanguage();
  const [form, setForm] = useState({});
  const employees = Object.entries(employeesRaw).map(([id, v]) => ({ id, name: v.name || '—' }));
  const effectiveEmployeeId = employeeId || (employees[0]?.id || '');
  const [targetId, setTargetId] = useState(effectiveEmployeeId);

  const emp = employeesRaw[targetId] || {};

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!companyUID || !targetId) return;
    const base = `companies/${companyUID}/employees/${targetId}`;
    if (type === 'legalDocs') {
      const r = ref(database, `${base}/legal/docs`);
      const newRef = push(r);
      await set(newRef, {
        type: form.docType || 'other',
        url: form.url || '',
        status: form.status || 'pending',
        uploadedAt: Date.now(),
        expiresAt: form.expiresAt ? Date.parse(form.expiresAt) : null,
        notes: form.notes || ''
      });
      await set(ref(database, `${base}/legal/lastReviewedAt`), Date.now());
    } else if (type === 'contractAmend') {
      // Validate required fields
      if (!form.newDepartment || !form.newDepartment.trim()) {
        alert(t('actionModal.validation.departmentRequired'));
        return;
      }

      // Prepare department/role changes, the amendment and any uploaded agreement
      // as one RTDB commit. Storage upload happens first because RTDB cannot include
      // file bytes, but no employee/contract state is partially published.
      const nextDepartment = (form.newDepartment || '').trim();
      const nextRole = (form.newRole || '').trim();
      const effectiveRole = nextRole || emp?.role || emp?.profile?.roleTitle || '';
      const workforceUpdates = {
        [`${base}/department`]: nextDepartment,
        [`${base}/roleDepartment`]: [effectiveRole, nextDepartment].filter(Boolean).join(' / ')
      };
      if (nextRole) workforceUpdates[`${base}/role`] = nextRole;

      // Optional: upload a new agreement file and attach
      let uploadedUrl = '';
      let uploadedStoragePath = '';
      const amendmentTimestamp = Date.now();
      if (form._amendFile) {
        const file = form._amendFile;
        const cleanName = (
          form.amendName
          || file.name.replace(/\.[^.]+$/, '')
          || t('actionModal.defaults.employmentContract')
        ).trim();
        const path = `companies/${companyUID}/employees/${targetId}/agreements/${amendmentTimestamp}_${file.name}`;
        const sref = stRef(storage, path);
        await uploadBytes(sref, file);
        const url = await getDownloadURL(sref);
        uploadedUrl = url;
        uploadedStoragePath = path;
        const agreementsRef = ref(database, `${base}/agreements`);
        const newAgreementRef = push(agreementsRef);
        workforceUpdates[`${base}/agreements/${newAgreementRef.key}`] = {
          name: cleanName,
          type: 'employment',
          downloadURL: url,
          storagePath: path,
          status: 'pending',
          uploadedAt: amendmentTimestamp,
          signedAt: null
        };
      }

      // Record the amendment entry
      const r = ref(database, `${base}/contract/amendments`);
      const newRef = push(r);
      workforceUpdates[`${base}/contract/amendments/${newRef.key}`] = {
        effectiveDate: form.effectiveDate || '',
        docUrl: uploadedUrl || '',
        storagePath: uploadedStoragePath || ''
      };
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId: targetId,
        source: 'employee_contract_amendment',
        changeKinds: ['occupation', 'contract']
      });
      await update(ref(database), workforceUpdates);
      try {
        await nationalizationService.syncEmployeeLifecycle(companyUID, targetId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_contract_amendment',
          changeKinds: ['occupation', 'contract']
        });
      } catch (syncError) {
        console.warn('Nationalization contract/occupation review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_contract_amendment',
        recalculated: false
      });
    } else if (type === 'ptoRequest') {
      // Validate dates and available balance
      if (!form.startDate || !form.endDate) {
        alert(t('actionModal.validation.ptoDatesRequired'));
        return;
      }
      const reqDays = (typeof inclusiveDaysBetween === 'function')
        ? inclusiveDaysBetween(form.startDate, form.endDate)
        : 0;
      const available = emp?.pto?.balanceDays ?? 0;
      if (reqDays <= 0) {
        alert(t('actionModal.validation.invalidDateRange'));
        return;
      }
      if (reqDays > available) {
        alert(t('actionModal.validation.ptoExceedsBalance', {
          requested: reqDays,
          available
        }));
        return;
      }

      const r = ref(database, `${base}/pto/requests`);
      const newRef = push(r);
      const payload = {
        id: newRef.key,
        type: form.ptoType || 'vacation',
        startDate: form.startDate || '',
        endDate: form.endDate || '',
        status: 'pending',
        requestedAt: Date.now(),
        approvedBy: null,
        comment: form.comment || '',
        days: reqDays,
      };
      await set(newRef, payload);

      // Notify manager via email if available
      try {
        const mgrId = emp?.managerId;
        if (mgrId) {
          const mgrSnap = await get(ref(database, `companies/${companyUID}/employees/${mgrId}`));
          const mgr = mgrSnap.val() || {};
          const managerEmail = mgr?.contact?.email || '';
          const managerName = mgr?.name || '';
          const employeeName = emp?.name || '';
          if (managerEmail) {
            await fetch(`${BACKEND_URL}/send-pto-request-email`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                companyId: companyUID,
                managerEmail,
                managerName,
                employeeName,
                request: {
                  id: payload.id,
                  type: payload.type,
                  startDate: payload.startDate,
                  endDate: payload.endDate,
                  days: payload.days,
                  comment: payload.comment || '',
                }
              })
            });
          }
        }
      } catch (e) {
        console.error('Failed to send PTO request email to manager:', e);
      }
    } else if (type === 'ptoBalance') {
      const cur = emp?.pto?.balanceDays ?? 0;
      const delta = Number(form.delta || 0);
      const next = cur + delta;
      await set(ref(database, `${base}/pto/balanceDays`), next);
    } else if (type === 'agreement') {
      // Upload file to storage then save record
      const file = form._file;
      if (!file) { alert(t('actionModal.validation.choosePdf')); return; }
      const cleanName = (form.name || file.name.replace(/\.[^.]+$/, '')).trim();
      const path = `companies/${companyUID}/employees/${targetId}/agreements/${Date.now()}_${file.name}`;
      const sref = stRef(storage, path);
      await uploadBytes(sref, file);
      const url = await getDownloadURL(sref);
      const r = ref(database, `${base}/agreements`);
      const newRef = push(r);
      await set(newRef, {
        name: cleanName,
        type: form.agreementType || 'other',
        downloadURL: url,
        storagePath: path,
        status: form.status || 'pending',
        uploadedAt: Date.now(),
        signedAt: form.status === 'signed' ? Date.now() : null
      });
    } else if (type === 'leave') {
      const r = ref(database, `${base}/leave/records`);
      const newRef = push(r);
      await set(newRef, {
        id: newRef.key,
        category: form.category || 'other',
        startDate: form.startDate || '',
        endDate: form.endDate || '',
        status: form.status || 'planned',
        notes: form.notes || ''
      });
    } else if (type === 'workEmail') {
      const email = ((form.email ?? emp?.contact?.workEmail ?? emp?.contact?.email) || '').trim();
      await set(ref(database, `${base}/contact/workEmail`), email);
      // keep legacy contact.email populated if personal not set
      const currentPersonal = emp?.contact?.personalEmail || '';
      if (!currentPersonal) {
        await set(ref(database, `${base}/contact/email`), email);
      }
    }
    onClose();
  };

  const titleMap = {
    legalDocs: t('actionModal.titles.legalDocs'),
    contractAmend: t('actionModal.titles.contractAmend'),
    ptoRequest: t('actionModal.titles.ptoRequest'),
    ptoBalance: t('actionModal.titles.ptoBalance'),
    workEmail: t('actionModal.titles.workEmail'),
    agreement: t('actionModal.titles.agreement'),
    leave: t('actionModal.titles.leave'),
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" dir={isRTL ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{titleMap[type] || t('actionModal.titles.default')}</h2>
          <div className="close" onClick={onClose} title={t('common:buttons.close')}>&times;</div>
        </div>
        <div className="form-section">
          {!employeeId && (
            <div className="input-group">
              <label htmlFor="employee-select" style={{ display: 'block', color: 'var(--app-heading, #031128)', marginBottom: 8 }}>{t('actionModal.employee')}</label>
              <select id="employee-select" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
          )}

          {type === 'legalDocs' && (
            <>
              <div className="input-group">
                <label style={{ display: 'block', color: 'var(--app-heading, #031128)', marginBottom: 8 }}>{t('actionModal.documentType')}</label>
                <select value={form.docType || ''} onChange={(e) => setField('docType', e.target.value)}>
                  <option value="id_card">{t('documents.types.id_card')}</option>
                  <option value="work_permit">{t('documents.types.work_permit')}</option>
                  <option value="visa">{t('documents.types.visa')}</option>
                  <option value="insurance">{t('documents.types.insurance')}</option>
                  <option value="other">{t('documents.types.other')}</option>
                </select>
              </div>
              <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.documentUrl')} value={form.url || ''} onChange={(e) => setField('url', e.target.value)} /></div>
              <div className="modal-row">
                <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.expiresAt')} value={form.expiresAt || ''} onChange={(e) => setField('expiresAt', e.target.value)} /></div>
                <div className="input-group">
                  <select value={form.status || 'pending'} onChange={(e) => setField('status', e.target.value)}>
                    <option value="pending">{t('actionModal.statuses.pending')}</option>
                    <option value="valid">{t('actionModal.statuses.valid')}</option>
                    <option value="expired">{t('actionModal.statuses.expired')}</option>
                  </select>
                </div>
              </div>
              <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.notesOptional')} value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
            </>
          )}

          {type === 'contractAmend' && (
            <>
              <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.effectiveDate')} value={form.effectiveDate || ''} onChange={(e) => setField('effectiveDate', e.target.value)} /></div>
              <div className="modal-row">
                <div className="input-group">
                  <select value={form.newDepartment || ''} onChange={(e) => setField('newDepartment', e.target.value)}>
                    <option value="">{t('actionModal.placeholders.selectDepartment')}</option>
                    {[
                      'Administration',
                      'Customer Support',
                      'Engineering',
                      'Product Management',
                      'Design / UX',
                      'Data & Analytics',
                      'Marketing',
                      'Sales',
                      'Finance & Accounting',
                      'Human Resources',
                      'Operations',
                      'IT / Systems',
                      'Security / InfoSec',
                      'Legal',
                      'Compliance',
                      'Procurement',
                      'Supply Chain',
                      'Logistics',
                      'Manufacturing',
                      'Quality Assurance',
                      'Research & Development',
                      'Business Development',
                      'Partnerships',
                      'Strategy',
                      'Content',
                      'Communications / PR',
                      'Community',
                      'Facilities',
                      'Real Estate',
                      'Construction',
                      'Healthcare',
                      'Education'
                    ].map((d) => (
                      <option key={d} value={d}>{translateDepartment(d, language)}</option>
                    ))}
                  </select>
                </div>
                <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.newRoleOptional')} value={form.newRole || ''} onChange={(e) => setField('newRole', e.target.value)} /></div>
              </div>
              <div className="modal-row">
                <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.agreementName')} value={form.amendName || t('actionModal.defaults.employmentContract')} onChange={(e) => setField('amendName', e.target.value)} /></div>
                <div className="input-group"><input type="file" accept="application/pdf" onChange={(e) => setField('_amendFile', (e.target.files && e.target.files[0]) || null)} /></div>
              </div>
            </>
          )}

          {type === 'workEmail' && (
            <>
              <div className="input-group">
                <input
                  type="email"
                  placeholder={t('actionModal.placeholders.workEmail')}
                  value={form.email ?? (emp?.contact?.workEmail || '')}
                  onChange={(e) => setField('email', e.target.value)}
                  style={{ background: '#FFFFFF', border: '1px solid rgba(3,17,40,0.2)', borderRadius: 8, padding: '12px 14px', color: 'var(--app-heading, #031128)' }}
                />
              </div>
              <span className="micro-subtext" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}>
                {t('actionModal.workEmailStored')}
              </span>
            </>
          )}

          {type === 'ptoRequest' && (
            <>
              <div className="modal-row">
                <div className="input-group">
                  <select value={form.ptoType || 'vacation'} onChange={(e) => setField('ptoType', e.target.value)}>
                    <option value="vacation">{t('pto.types.vacation')}</option>
                    <option value="sick">{t('pto.types.sick')}</option>
                    <option value="personal">{t('pto.types.personal')}</option>
                    <option value="unpaid">{t('pto.types.unpaid')}</option>
                  </select>
                </div>
                <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.startDate')} value={form.startDate || ''} onChange={(e) => setField('startDate', e.target.value)} /></div>
                <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.endDate')} value={form.endDate || ''} onChange={(e) => setField('endDate', e.target.value)} /></div>
              </div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
                <span className="micro-subtext" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}>{t('actionModal.pto.remaining', { count: emp?.pto?.balanceDays ?? 0 })}</span>
                <span className="micro-subtext" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}>
                  {t('actionModal.pto.requested', { count: (() => {
                    const d = (typeof inclusiveDaysBetween === 'function') ? inclusiveDaysBetween(form.startDate, form.endDate) : 0;
                    return d > 0 ? d : '—';
                  })() })}
                </span>
                {(emp?.managerId) && (
                  <span className="micro-subtext" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}>
                    {t('actionModal.pto.notifyManager')}
                  </span>
                )}
              </div>
              <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.commentOptional')} value={form.comment || ''} onChange={(e) => setField('comment', e.target.value)} /></div>
            </>
          )}

          {type === 'ptoBalance' && (
            <>
              <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.ptoDelta', { current: emp?.pto?.balanceDays ?? 0 })} value={form.delta || ''} onChange={(e) => setField('delta', e.target.value)} /></div>
            </>
          )}

          {/* approvals and attendance UI removed for now */}

          {type === 'agreement' && (
            <>
              <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.agreementName')} value={form.name || ''} onChange={(e) => setField('name', e.target.value)} /></div>
              <div className="modal-row">
                <div className="input-group">
                  <select value={form.agreementType || 'other'} onChange={(e) => setField('agreementType', e.target.value)}>
                    <option value="nda">{t('actionModal.agreementTypes.nda')}</option>
                    <option value="employment">{t('actionModal.agreementTypes.employment')}</option>
                    <option value="ip">{t('actionModal.agreementTypes.ip')}</option>
                    <option value="policy">{t('actionModal.agreementTypes.policy')}</option>
                    <option value="other">{t('actionModal.agreementTypes.other')}</option>
                  </select>
                </div>
                <div className="input-group">
                  <select value={form.status || 'pending'} onChange={(e) => setField('status', e.target.value)}>
                    <option value="pending">{t('actionModal.statuses.pending')}</option>
                    <option value="signed">{t('actionModal.statuses.signed')}</option>
                    <option value="obsolete">{t('actionModal.statuses.obsolete')}</option>
                  </select>
                </div>
              </div>
              <div className="input-group">
                <input type="file" accept="application/pdf" onChange={(e) => setField('_file', (e.target.files && e.target.files[0]) || null)} />
              </div>
            </>
          )}

          {type === 'leave' && (
            <>
              <div className="modal-row">
                <div className="input-group">
                  <select value={form.category || 'other'} onChange={(e) => setField('category', e.target.value)}>
                    <option value="maternity">{t('actionModal.leaveTypes.maternity')}</option>
                    <option value="paternity">{t('actionModal.leaveTypes.paternity')}</option>
                    <option value="bereavement">{t('actionModal.leaveTypes.bereavement')}</option>
                    <option value="jury_duty">{t('actionModal.leaveTypes.jury_duty')}</option>
                    <option value="medical">{t('actionModal.leaveTypes.medical')}</option>
                    <option value="other">{t('actionModal.leaveTypes.other')}</option>
                  </select>
                </div>
                <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.startDate')} value={form.startDate || ''} onChange={(e) => setField('startDate', e.target.value)} /></div>
                <div className="input-group"><input type="date" placeholder={t('actionModal.placeholders.endDate')} value={form.endDate || ''} onChange={(e) => setField('endDate', e.target.value)} /></div>
              </div>
              <div className="modal-row">
                <div className="input-group">
                  <select value={form.status || 'planned'} onChange={(e) => setField('status', e.target.value)}>
                    <option value="planned">{t('actionModal.statuses.planned')}</option>
                    <option value="ongoing">{t('actionModal.statuses.ongoing')}</option>
                    <option value="completed">{t('actionModal.statuses.completed')}</option>
                  </select>
                </div>
                <div className="input-group"><input type="text" placeholder={t('actionModal.placeholders.notesOptional')} value={form.notes || ''} onChange={(e) => setField('notes', e.target.value)} /></div>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>{t('common:buttons.cancel')}</button>
          <button className="btn-primary" onClick={save}>{t('common:buttons.save')}</button>
        </div>
      </div>
    </div>
  );
};

// Inline agreements gallery for a single employee (no popup)
const AgreementsGallery = ({ employeeId, employeesRaw, companyUID, onBack }) => {
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const emp = employeesRaw[employeeId] || {};
  const name = emp.name || '—';
  const agreementsObj = emp.agreements || {};
  const agreements = Array.isArray(agreementsObj)
    ? agreementsObj.map((a, idx) => ({ id: String(idx), ...(a || {}) }))
    : Object.entries(agreementsObj).map(([id, v]) => ({ id, ...(v || {}) }));

  const [typeFilter, setTypeFilter] = useState('all');
  const presentTypes = Array.from(new Set(agreements.map(a => String(a.type || 'other').toLowerCase())));
  const filteredAgreements = typeFilter === 'all'
    ? agreements
    : agreements.filter(a => String(a.type || 'other').toLowerCase() === typeFilter);

  // Unknown legacy types remain readable while known canonical values are translated.
  const agreementTypeLabel = (value) => {
    const normalized = String(value || 'other').toLowerCase();
    const fallback = normalized
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    return t(`agreements.gallery.types.${normalized}`, { defaultValue: fallback });
  };

  const [signTarget, setSignTarget] = useState(null);

  const changeStatus = async (ag, next) => {
    const base = `companies/${companyUID}/employees/${employeeId}/agreements/${ag.id}`;
    const workforceUpdates = {
      [`${base}/status`]: next,
      [`${base}/signedAt`]: next === 'signed' ? Date.now() : null
    };
    const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
      companyUID,
      employeeId,
      source: 'employee_agreement_status',
      changeKinds: ['contract']
    });
    await update(ref(database), workforceUpdates);
    try {
      await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
        eventId: lifecycleEvent.eventId,
        source: 'employee_agreement_status',
        changeKinds: ['contract']
      });
    } catch (syncError) {
      console.warn('Nationalization contract review is pending:', syncError);
    }
    announceNationalizationWorkforceChange({
      companyUID,
      source: 'employee_agreement_status',
      recalculated: false
    });
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ padding: '16px' }}>
      <div className="agreements-gallery-header">
        <button className="post-job-cta" onClick={onBack}><i className={`fa-solid fa-arrow-${isRTL ? 'right' : 'left'}`}></i><span>{t('common:buttons.back')}</span></button>
        <div className="emp-name-row"><LetterBadge name={name} photoUrl={emp.profile?.photoUrl || emp.profile?.photoURL || emp.photoUrl || emp.photoURL || ''} /><span className="emp-name-big">{name}</span></div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
          <span className="micro-subtext" style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.9 }}>{t('agreements.gallery.filterByType')}</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={{
              background: 'var(--app-surface, rgba(3, 17, 40, 0.6))',
              color: 'var(--app-text, #FFFFFF)',
              border: "1px solid var(--app-border, rgba(255,255,255,0.2))",
              borderRadius: 6,
              padding: '6px 8px'
            }}
          >
            <option value="all">{t('agreements.gallery.allTypes')}</option>
            {presentTypes.map((agreementType) => (
              <option key={agreementType} value={agreementType}>{agreementTypeLabel(agreementType)}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="emp-docs-grid">
        {filteredAgreements.length === 0 ? (
          <div style={{ color: 'var(--app-text, #FFFFFF)', opacity: 0.8 }}>{t('agreements.gallery.empty')}</div>
        ) : filteredAgreements.map(ag => (
          <AgreementCard
            key={ag.id}
            ag={ag}
            employeeId={employeeId}
            companyUID={companyUID}
            onChangeStatus={changeStatus}
            onRequestUploadSigned={(ag2) => setSignTarget(ag2)}
          />
        ))}
      </div>
      {signTarget && (
        <SignedUploadModal
          ag={signTarget}
          employeeId={employeeId}
          companyUID={companyUID}
          onClose={() => setSignTarget(null)}
        />
      )}
    </div>
  );
};

// Single doc card with preview + kebab menu
const AgreementCard = ({ ag, employeeId, companyUID, onChangeStatus, onRequestUploadSigned }) => {
  const { t } = useTranslation('myteam');
  const [menuOpen, setMenuOpen] = useState(false);

  const docType = String(ag.type || 'other').toLowerCase();
  const docTypeFallback = docType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const docTypeLabel = t(`agreements.gallery.types.${docType}`, { defaultValue: docTypeFallback });
  const agreementStatus = String(ag.status || 'pending').toLowerCase();
  const statusFallback = agreementStatus
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const statusLabel = t(`agreements.gallery.statuses.${agreementStatus}`, { defaultValue: statusFallback });

  const statusClass = agreementStatus === 'signed' ? 'emp-status-signed' : agreementStatus === 'obsolete' ? 'emp-status-obsolete' : 'emp-status-pending';
  const link = ag.downloadURL || ag.url;

  return (
    <div className="emp-doc-card" onClick={() => { if (link) window.open(link, '_blank', 'noopener,noreferrer'); }}>
      {(() => {
        const link = ag.downloadURL || ag.url; return (
          <div className={`emp-doc-preview ${link ? 'embed' : ''}`} onClick={() => { if (link) window.open(link, '_blank', 'noopener,noreferrer'); }} title={link ? t('agreements.gallery.openPdf') : t('agreements.gallery.noPreview')}>
            {link ? (
              <embed src={link} type="application/pdf" className="pdf-embed" />
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.7 }}>{t('agreements.gallery.noPreview')}</div>
            )}
          </div>);
      })()}
      <div className="emp-doc-title">{ag.name || t('agreements.gallery.untitled')}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className={`emp-doc-type emp-type-${docType}`}>{docTypeLabel}</span>
          <span className={`emp-doc-status ${statusClass}`}>{uiText(statusLabel)}</span>
        </div>
        <button className="emp-card-menu-btn" onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }} title={t('agreements.gallery.actions')}><i className="fa-solid fa-ellipsis-vertical"></i></button>
      </div>
      {menuOpen && (
        <div
          className="emp-card-actions"
          onMouseLeave={() => setMenuOpen(false)}
          onClick={(e) => { e.stopPropagation(); }}
          onMouseDown={(e) => { e.stopPropagation(); }}
        >
          {ag.status === 'pending' && (
            <>
              <button className="emp-card-action" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); onRequestUploadSigned && onRequestUploadSigned(ag); }}><i className="fa-solid fa-file-signature" style={{ color: '#568ded', marginInlineEnd: 6 }}></i> {t('agreements.gallery.markSigned')}</button>
            </>
          )}
          {ag.status === 'signed' && (
            <button className="emp-card-action" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChangeStatus(ag, 'obsolete'); setMenuOpen(false); }}><i className="fa-solid fa-ban" style={{ color: '#568ded', marginInlineEnd: 6 }}></i> {t('agreements.gallery.markObsolete')}</button>
          )}
        </div>
      )}
    </div>
  );
};

export default MyTeam;

const ReviewRequestModal = ({ companyUID, employeeId, employeeName, requests, onClose }) => {
  const { t } = useTranslation(['myteam', 'common']);
  const { language } = useLanguage();
  const [processing, setProcessing] = useState(null);
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';

  const handleAction = async (req, action) => {
    if (!companyUID || !employeeId) return;
    setProcessing(req.id);

    try {
      const basePath = `companies/${companyUID}/employees/${employeeId}`;
      const status = action === 'approve' ? 'approved' : 'rejected';

      // 1. Update request status
      await update(ref(database, `${basePath}/requests/${req.id}`), {
        status,
        reviewedAt: Date.now(),
        reviewedBy: 'HR'
      });

      if (status === 'approved') {
        // 2. Apply changes based on type
        if (req.type === 'salary') {
          const workforceUpdates = {
            [`${basePath}/compensation/base`]: Number(req.amount)
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source: 'employee_salary_change',
            changeKinds: ['compensation']
          });
          await update(ref(database), workforceUpdates);
          try {
            await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source: 'employee_salary_change',
              changeKinds: ['compensation']
            });
          } catch (syncError) {
            console.warn('Nationalization salary review is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source: 'employee_salary_change',
            recalculated: false
          });
        } else if (req.type === 'bonus') {
          const bonusRef = push(ref(database, `${basePath}/compensation/bonuses`));
          const source = 'employee_bonus_approval';
          const workforceUpdates = {
            [`${basePath}/compensation/bonuses/${bonusRef.key}`]: {
              amount: Number(req.amount),
              reason: req.reason,
              date: Date.now(),
              approvedBy: 'HR'
            }
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source,
            changeKinds: ['compensation']
          });
          await update(ref(database), workforceUpdates);
          let lifecycleSync = null;
          try {
            lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source,
              changeKinds: ['compensation']
            });
          } catch (syncError) {
            console.warn('Nationalization bonus review is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source,
            recalculated: false,
            syncPending: !lifecycleSync
          });
        } else if (req.type === 'offboarding') {
          const workforceUpdates = {
            [`${basePath}/lifecycle/status`]: 'offboarding',
            [`${basePath}/lifecycle/terminationDate`]: req.date,
            [`${basePath}/status`]: 'offboarding'
          };
          const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
            companyUID,
            employeeId,
            source: 'employee_offboarding_approval',
            changeKinds: ['lifecycle']
          });
          await update(ref(database), workforceUpdates);
          let lifecycleSync = null;
          try {
            lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
              eventId: lifecycleEvent.eventId,
              source: 'employee_offboarding_approval',
              changeKinds: ['lifecycle']
            });
          } catch (syncError) {
            console.warn('Nationalization offboarding sync is pending:', syncError);
          }
          announceNationalizationWorkforceChange({
            companyUID,
            source: 'employee_offboarding_approval',
            recalculated: false,
            syncPending: !lifecycleSync
          });
        }
      }

      // 3. Notify Employee
      const notifRef = push(ref(database, `${basePath}/notifications`));
      const requestTypeLabel = t(`requestsModal.types.${req.type}`, req.type);
      const title = t(`requestsModal.notificationTitles.${status}`, status);
      const message = t(`requestsModal.notificationMessages.${status}`, {
        type: requestTypeLabel,
        defaultValue: `Your ${requestTypeLabel} request has been ${status}.`
      });
      await set(notifRef, {
        title,
        message,
        type: status === 'approved' ? 'success' : 'error',
        timestamp: Date.now(),
        read: false
      });

      // 4. Send Email Notification to Employee
      const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";
      try {
        const token = await auth.currentUser?.getIdToken();
        await fetch(`${backendUrl}/send-notification-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            companyId: companyUID,
            employeeId,
            subject: title,
            message,
            type: status === 'approved' ? 'success' : 'error'
          })
        });
      } catch (e) {
        console.error("Employee email send failed", e);
      }

      // 5. Notify the Manager who made the original request
      if (req.requestedBy) {
        try {
          const token = await auth.currentUser?.getIdToken();
          await fetch(`${backendUrl}/notify-manager-hr-decision`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              companyId: companyUID,
              employeeId,
              managerId: req.requestedBy,
              requestType: req.type,
              decision: status,
              employeeName,
              locale: language === 'ar' ? 'ar' : 'en'
            })
          });
        } catch (e) {
          console.error("Manager notification failed", e);
        }
      }

      // 6. Log Activity
      await push(ref(database, `${basePath}/activities`), {
        type: status === 'approved' ? 'request_approved' : 'request_rejected',
        description: t(`requestsModal.activityMessages.${status}`, {
          type: requestTypeLabel,
          defaultValue: `${requestTypeLabel} request ${status} by HR`
        }),
        timestamp: Date.now(),
        by: 'HR'
      });

      // Refresh logic is implicit via realtime listener
      // If we rejected/approved, it's no longer 'pending_hr' so it will disappear from list

    } catch (e) {
      console.error('Action failed', e);
      alert(uiText('Action failed'));
    } finally {
      setProcessing(null);
      // If this was the last pending request, we might want to close, but user might want to see history?
      // For now, let user close manually or we can check remaining
    }
  };

  const pendingList = requests.filter(r => r.status === 'pending_hr');

  useEffect(() => {
    if (pendingList.length === 0) {
      // Auto close if no requests left
      const timer = setTimeout(onClose, 1000);
      return () => clearTimeout(timer);
    }
  }, [pendingList.length, onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('requestsModal.title', { name: employeeName, defaultValue: `Review Requests for ${employeeName}` })}</h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>
        <div className="form-section">
          {pendingList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, opacity: 0.6 }}>
              <i className="fa-solid fa-check-circle" style={{ fontSize: 24, marginBottom: 10, display: 'block' }}></i>
              {t('requestsModal.allReviewed', 'All requests reviewed!')}
            </div>
          ) : pendingList.map(req => (
            <div key={req.id} style={{
              background: 'var(--app-surface, rgba(255,255,255,0.05))',
              padding: 16,
              borderRadius: 8,
              marginBottom: 12,
              border: "1px solid var(--app-border, rgba(255,255,255,0.1))"
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
                <span style={{ fontWeight: 600, textTransform: 'capitalize', color: '#568ded' }}>{req.type}</span>
                <span style={{ opacity: 0.7, fontSize: 12 }}>{new Date(req.requestedAt).toLocaleDateString(locale)}</span>
              </div>
              {req.amount && <div style={{ marginBottom: 4 }}>{t('requestsModal.requestedAmount', 'Requested Amount:')} <strong>${Number(req.amount).toLocaleString()}</strong></div>}
              {req.date && <div style={{ marginBottom: 4 }}>{t('requestsModal.proposedDate', 'Proposed Date:')} <strong>{req.date}</strong></div>}
              {req.reason && <div style={{ marginBottom: 8, fontStyle: 'italic', opacity: 0.8, background: 'rgba(0,0,0,0.2)', padding: 8, borderRadius: 4 }}>"{req.reason}"</div>}
              {req.requestedByName && <div style={{ fontSize: 12, opacity: 0.6 }}>{t('requestsModal.requestedBy', { name: req.requestedByName, defaultValue: `Requested by: ${req.requestedByName}` })}</div>}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  className="btn-secondary"
                  style={{ borderColor: '#EB4040', color: '#EB4040' }}
                  onClick={() => handleAction(req, 'reject')}
                  disabled={processing === req.id}
                >
                  {t('requestsModal.reject', 'Reject')}
                </button>
                <button
                  className="btn-primary"
                  style={{ '--light-control-fill': '#40EB6E', background: '#40EB6E', color: "var(--app-neutral-ink, #000)", border: 'none' }}
                  onClick={() => handleAction(req, 'approve')}
                  disabled={processing === req.id}
                  data-light-control={"filled"}>
                  {t('requestsModal.approve', 'Approve')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
